import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useTenant } from './contexts/TenantContext';
import axios from './lib/axiosClient';
import {
  Building2, TrendingUp, UserPlus, Target, Megaphone,
  Activity, PieChart, Shield, Search, Plus, Eye,
  DollarSign, Users, Globe, Calendar, ArrowUpRight,
  CheckCircle, Clock, AlertCircle, Edit2,
  Trash2, Mail, X, Save,
  MoreVertical, RefreshCw, Upload, Download, Phone, MessageSquare, ArrowRight, ExternalLink,
} from 'lucide-react';

function PageShell({ title, icon: Icon, children, action }) {
  return (
    <div style={{ padding: '24px', maxWidth: 1200, margin: '0 auto' }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 24, flexWrap: 'wrap', gap: 12 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <Icon size={28} strokeWidth={1.8} style={{ color: '#a78bfa' }} />
          <h1 style={{ fontSize: 24, fontWeight: 700, color: '#f1f5f9', margin: 0 }}>{title}</h1>
        </div>
        {action}
      </div>
      {children}
    </div>
  );
}

function StatCard({ label, value, icon: Icon, color, sub, onClick }) {
  return (
    <div onClick={onClick} style={{
      background: '#1e293b', borderRadius: 12, padding: 20,
      border: '1px solid #334155', cursor: onClick ? 'pointer' : 'default',
      transition: 'border-color 0.15s',
    }}
    onMouseEnter={function(e) { if (onClick) e.currentTarget.style.borderColor = color || '#a78bfa'; }}
    onMouseLeave={function(e) { e.currentTarget.style.borderColor = '#334155'; }}
    >
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
        <div>
          <div style={{ color: '#94a3b8', fontSize: 13, marginBottom: 4 }}>{label}</div>
          <div style={{ color: '#f1f5f9', fontSize: 28, fontWeight: 700 }}>{value}</div>
          {sub && <div style={{ color: '#64748b', fontSize: 12, marginTop: 4 }}>{sub}</div>}
        </div>
        <div style={{
          width: 40, height: 40, borderRadius: 10,
          background: (color || '#a78bfa') + '22',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
        }}>
          <Icon size={20} style={{ color: color || '#a78bfa' }} />
        </div>
      </div>
    </div>
  );
}

function SearchBar({ value, onChange, placeholder }) {
  return (
    <div style={{
      display: 'flex', alignItems: 'center', gap: 8, background: '#1e293b',
      borderRadius: 8, padding: '8px 12px', border: '1px solid #334155',
    }}>
      <Search size={16} style={{ color: '#64748b' }} />
      <input placeholder={placeholder || 'Search...'} value={value}
        onChange={function(e) { onChange(e.target.value); }}
        style={{ background: 'transparent', border: 'none', color: '#f1f5f9', fontSize: 14, outline: 'none', width: '100%' }} />
      {value && <X size={14} style={{ color: '#64748b', cursor: 'pointer' }} onClick={function() { onChange(''); }} />}
    </div>
  );
}

function Badge({ text, color }) {
  return (<span style={{ padding: '3px 10px', borderRadius: 12, fontSize: 12, fontWeight: 600, background: color + '22', color: color }}>{text}</span>);
}

function Modal({ title, onClose, children }) {
  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.7)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 9999 }} onClick={onClose}>
      <div style={{ background: '#1e293b', borderRadius: 16, padding: 24, border: '1px solid #334155', width: 480, maxWidth: '95vw', maxHeight: '85vh', overflowY: 'auto' }} onClick={function(e) { e.stopPropagation(); }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
          <h2 style={{ color: '#f1f5f9', fontSize: 18, fontWeight: 700, margin: 0 }}>{title}</h2>
          <X size={20} style={{ color: '#64748b', cursor: 'pointer' }} onClick={onClose} />
        </div>
        {children}
      </div>
    </div>
  );
}

function FormField({ label, value, onChange, type, placeholder, options }) {
  var s = { background: '#0f172a', border: '1px solid #334155', borderRadius: 8, padding: '10px 12px', color: '#f1f5f9', fontSize: 14, width: '100%', outline: 'none' };
  return (
    <div style={{ marginBottom: 14 }}>
      <label style={{ color: '#94a3b8', fontSize: 13, fontWeight: 600, display: 'block', marginBottom: 4 }}>{label}</label>
      {type === 'select' ? (
        <select value={value} onChange={function(e) { onChange(e.target.value); }} style={s}>
          {options.map(function(o) { return <option key={o.value} value={o.value}>{o.label}</option>; })}
        </select>
      ) : type === 'textarea' ? (
        <textarea value={value} onChange={function(e) { onChange(e.target.value); }} style={Object.assign({}, s, { minHeight: 80, resize: 'vertical' })} placeholder={placeholder} />
      ) : (
        <input type={type || 'text'} value={value} onChange={function(e) { onChange(e.target.value); }} style={s} placeholder={placeholder} />
      )}
    </div>
  );
}

function Btn({ children, onClick, color, outline, small, disabled }) {
  var bg = outline ? 'transparent' : (color || '#a78bfa');
  return (
    <button onClick={onClick} disabled={disabled} style={{
      display: 'inline-flex', alignItems: 'center', gap: 6,
      background: bg, color: outline ? (color || '#a78bfa') : '#fff',
      border: outline ? '1px solid ' + (color || '#a78bfa') : 'none',
      borderRadius: 8, padding: small ? '6px 12px' : '10px 18px',
      cursor: disabled ? 'not-allowed' : 'pointer', fontWeight: 600, fontSize: small ? 12 : 14, opacity: disabled ? 0.5 : 1,
    }}>{children}</button>
  );
}

// ════════════════════════════════════════════════════════════════
// ALL BUSINESSES
// ════════════════════════════════════════════════════════════════
export function AdminBusinesses() {
  var { isSuperAdmin } = useTenant();
  var [tenants, setTenants] = useState([]);
  var [search, setSearch] = useState('');
  var [filter, setFilter] = useState('all');
  var [loading, setLoading] = useState(true);
  var [actionMenu, setActionMenu] = useState(null);
  var [expanded, setExpanded] = useState(null);
  var [editTenant, setEditTenant] = useState(null);
  var [selected, setSelected] = useState([]);
  var navigate = useNavigate();

  useEffect(function() { if (isSuperAdmin) loadTenants(); }, [isSuperAdmin]);

  var loadTenants = function() {
    axios.get('/api/admin/tenants')
      .then(function(r) { setTenants(r.data.tenants || r.data.data || []); setLoading(false); })
      .catch(function() { setLoading(false); });
  };

  var filtered = tenants.filter(function(t) {
    var m = (t.business_name || '').toLowerCase().includes(search.toLowerCase()) ||
            (t.subdomain || '').toLowerCase().includes(search.toLowerCase()) ||
            (t.email || '').toLowerCase().includes(search.toLowerCase());
    if (filter === 'all') return m;
    if (filter === 'active') return m && t.status === 'active';
    if (filter === 'trial') return m && t.status === 'trial';
    if (filter === 'paying') return m && (t.plan === 'founding' || t.plan === 'growth' || t.plan === 'professional');
    return m;
  });

  var loginAs = function(t) {
    localStorage.setItem('sa_tenant_override', String(t.id));
    window.location.href = '/app/dashboard';
  };

  var extendTrial = function(t) {
    axios.post('/api/admin/tenants/' + t.id + '/extend-trial', { days: 14 })
      .then(function() { loadTenants(); setActionMenu(null); })
      .catch(function(e) { alert('Error: ' + (e.response?.data?.error || e.message)); });
  };

  var deleteTenant = function(t) {
    if (!window.confirm('DELETE ' + t.business_name + '?\n\nThis will permanently delete ALL their data including bookings, customers, work orders, and invoices.\n\nThis cannot be undone!')) return;
    if (!window.confirm('Are you SURE? Type the business name to confirm would be ideal, but for now: click OK to permanently delete ' + t.business_name)) return;
    axios.delete('/api/admin/tenants/' + t.id)
      .then(function() { loadTenants(); })
      .catch(function(e) { alert('Error: ' + (e.response?.data?.error || e.message)); });
  };

  var toggleSelect = function(id) {
    setSelected(selected.includes(id) ? selected.filter(function(s) { return s !== id; }) : selected.concat([id]));
  };
  var toggleAll = function() {
    setSelected(selected.length === filtered.length ? [] : filtered.map(function(t) { return t.id; }));
  };
  var bulkDelete = function() {
    if (!window.confirm('DELETE ' + selected.length + ' businesses?\n\nThis permanently removes ALL their data.\nThis cannot be undone!')) return;
    if (!window.confirm('Last chance — permanently delete ' + selected.length + ' businesses?')) return;
    Promise.all(selected.map(function(id) { return axios.delete('/api/admin/tenants/' + id); }))
      .then(function() { setSelected([]); loadTenants(); })
      .catch(function(e) { alert('Error: ' + (e.response?.data?.error || e.message)); loadTenants(); });
  };

  var updateTenant = function(updated) {
    axios.put('/api/admin/tenants/' + updated.id, updated)
      .then(function() { loadTenants(); setEditTenant(null); })
      .catch(function(e) { alert('Error: ' + (e.response?.data?.error || e.message)); });
  };

  var statusColor = { active: '#22c55e', trial: '#f59e0b', suspended: '#ef4444', cancelled: '#64748b' };
  var counts = {
    all: tenants.length,
    active: tenants.filter(function(t) { return t.status === 'active'; }).length,
    trial: tenants.filter(function(t) { return t.status === 'trial'; }).length,
    paying: tenants.filter(function(t) { return t.plan === 'founding' || t.plan === 'growth' || t.plan === 'professional'; }).length,
  };

  return (
    <PageShell title="All Businesses" icon={Building2} action={
      <Btn onClick={function() { navigate('/app/admin'); }}><Plus size={16} /> New Business</Btn>
    }>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 16, marginBottom: 24 }}>
        <StatCard label="Total" value={counts.all} icon={Building2} color="#a78bfa" onClick={function() { setFilter('all'); }} />
        <StatCard label="Active" value={counts.active} icon={CheckCircle} color="#22c55e" onClick={function() { setFilter('active'); }} />
        <StatCard label="Trials" value={counts.trial} icon={Clock} color="#f59e0b" onClick={function() { setFilter('trial'); }} />
        <StatCard label="Paying" value={counts.paying} icon={DollarSign} color="#3b82f6" onClick={function() { setFilter('paying'); }} />
      </div>
      <div style={{ display: 'flex', gap: 12, marginBottom: 16, flexWrap: 'wrap' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <input type="checkbox" checked={filtered.length > 0 && selected.length === filtered.length} onChange={toggleAll}
            style={{ width: 16, height: 16, cursor: 'pointer', accentColor: '#a78bfa' }} title="Select all" />
        </div>
        <div style={{ flex: 1, minWidth: 200 }}><SearchBar value={search} onChange={setSearch} placeholder="Search businesses, emails..." /></div>
        <div style={{ display: 'flex', gap: 6 }}>
          {['all', 'active', 'trial', 'paying'].map(function(f) {
            return (<button key={f} onClick={function() { setFilter(f); }} style={{
              padding: '8px 14px', borderRadius: 8, fontSize: 13, fontWeight: 600,
              background: filter === f ? '#a78bfa' : '#1e293b', color: filter === f ? '#fff' : '#94a3b8',
              border: filter === f ? 'none' : '1px solid #334155', cursor: 'pointer', textTransform: 'capitalize',
            }}>{f} ({counts[f]})</button>);
          })}
        </div>
      </div>
      {selected.length > 0 && (
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '12px 20px', background: '#7f1d1d', borderRadius: '12px 12px 0 0', marginBottom: -1 }}>
          <span style={{ color: '#fca5a5', fontWeight: 600, fontSize: 14 }}>{selected.length} business{selected.length > 1 ? 'es' : ''} selected</span>
          <div style={{ display: 'flex', gap: 8 }}>
            <Btn small outline color="#fca5a5" onClick={function() { setSelected([]); }}>Cancel</Btn>
            <Btn small color="#ef4444" onClick={bulkDelete}><Trash2 size={14} /> Delete Selected</Btn>
          </div>
        </div>
      )}
      <div style={{ background: '#1e293b', borderRadius: selected.length > 0 ? '0 0 12px 12px' : 12, border: '1px solid #334155', overflow: 'hidden' }}>
        {loading ? (<div style={{ padding: 40, textAlign: 'center', color: '#64748b' }}>Loading...</div>) :
         filtered.length === 0 ? (<div style={{ padding: 40, textAlign: 'center', color: '#64748b' }}>No businesses found</div>) :
         filtered.map(function(t, idx) {
          var score = t.setupScore || 0;
          var sc = score >= 80 ? '#22c55e' : score >= 40 ? '#f59e0b' : '#ef4444';
          var isExp = expanded === t.id;
          return (
            <div key={t.id} style={{ borderBottom: idx < filtered.length - 1 ? '1px solid #334155' : 'none', borderLeft: isExp ? '3px solid #a78bfa' : '3px solid transparent', transition: 'all 0.2s' }}>
              <div onClick={function() { setExpanded(isExp ? null : t.id); }} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '14px 20px', cursor: 'pointer' }}
                onMouseEnter={function(e) { e.currentTarget.style.background = '#1e293b80'; }}
                onMouseLeave={function(e) { e.currentTarget.style.background = 'transparent'; }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
                  <input type="checkbox" checked={selected.includes(t.id)} onChange={function() { toggleSelect(t.id); }} onClick={function(e) { e.stopPropagation(); }}
                    style={{ width: 16, height: 16, cursor: 'pointer', accentColor: '#a78bfa' }} />
                  <div style={{ width: 40, height: 40, borderRadius: 10, background: (statusColor[t.status] || '#64748b') + '22', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 16, fontWeight: 800, color: statusColor[t.status] || '#64748b' }}>
                    {(t.business_name || '?')[0].toUpperCase()}
                  </div>
                  <div>
                    <div style={{ color: '#f1f5f9', fontWeight: 600, fontSize: 15 }}>{t.business_name}</div>
                    <div style={{ color: '#64748b', fontSize: 12 }}>{t.subdomain} · {t.business_type || 'other'} · {t.email || ''}</div>
                  </div>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                  <div style={{ width: 50 }}>
                    <div style={{ fontSize: 10, color: sc, fontWeight: 600, marginBottom: 2 }}>{score}%</div>
                    <div style={{ height: 4, background: '#334155', borderRadius: 2 }}><div style={{ height: '100%', width: score + '%', background: sc, borderRadius: 2 }} /></div>
                  </div>
                  <Badge text={t.status || 'unknown'} color={statusColor[t.status] || '#64748b'} />
                  <Badge text={t.plan || 'none'} color="#94a3b8" />
                  <span style={{ color: '#64748b', fontSize: 16, transition: 'transform .2s', transform: isExp ? 'rotate(180deg)' : 'rotate(0)', display: 'inline-block' }}>▾</span>
                </div>
              </div>
              {isExp && (
                <div style={{ background: '#0f172a', borderTop: '1px solid #334155', padding: '16px 20px' }}>
                  <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 16 }}>
                    <button onClick={function() { loginAs(t); }} style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '8px 16px', background: '#a78bfa22', color: '#a78bfa', border: 'none', borderRadius: 8, fontSize: 13, fontWeight: 600, cursor: 'pointer' }}><Eye size={14} /> Login As</button>
                    {t.subdomain && <a href={'/site/' + t.subdomain} target="_blank" rel="noreferrer" style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '8px 16px', background: '#3b82f622', color: '#60a5fa', border: 'none', borderRadius: 8, fontSize: 13, fontWeight: 600, cursor: 'pointer', textDecoration: 'none' }}><Globe size={14} /> Website</a>}
                    {t.subdomain && <a href={'/book/' + t.subdomain} target="_blank" rel="noreferrer" style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '8px 16px', background: '#f9731622', color: '#fb923c', border: 'none', borderRadius: 8, fontSize: 13, fontWeight: 600, cursor: 'pointer', textDecoration: 'none' }}><Calendar size={14} /> Booking</a>}
                    <button onClick={function() { setEditTenant(t); }} style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '8px 16px', background: '#22c55e22', color: '#4ade80', border: 'none', borderRadius: 8, fontSize: 13, fontWeight: 600, cursor: 'pointer' }}><Edit2 size={14} /> Edit</button>
                    {t.status === 'trial' && <button onClick={function() { extendTrial(t); }} style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '8px 16px', background: '#f59e0b22', color: '#fbbf24', border: 'none', borderRadius: 8, fontSize: 13, fontWeight: 600, cursor: 'pointer' }}><Clock size={14} /> Extend Trial</button>}
                    <button onClick={function() { deleteTenant(t); }} style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '8px 16px', background: '#ef444422', color: '#f87171', border: 'none', borderRadius: 8, fontSize: 13, fontWeight: 600, cursor: 'pointer' }}><Trash2 size={14} /> Delete</button>
                  </div>
                  <div style={{ display: 'flex', gap: 24, flexWrap: 'wrap', fontSize: 13 }}>
                    {t.email && <div><span style={{ color: '#64748b' }}>Email: </span><span style={{ color: '#60a5fa' }}>{t.email}</span></div>}
                    {t.phone && <div><span style={{ color: '#64748b' }}>Phone: </span><span style={{ color: '#a78bfa' }}>{t.phone}</span></div>}
                    {t.subdomain && <div><span style={{ color: '#64748b' }}>Subdomain: </span><span style={{ color: '#94a3b8' }}>{t.subdomain}</span></div>}
                    {t.created_at && <div><span style={{ color: '#64748b' }}>Created: </span><span style={{ color: '#94a3b8' }}>{new Date(t.created_at).toLocaleDateString()}</span></div>}
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>
      {editTenant && <EditTenantModal tenant={editTenant} onSave={updateTenant} onClose={function() { setEditTenant(null); }} />}
    </PageShell>
  );
}

function EditTenantModal({ tenant, onSave, onClose }) {
  var [form, setForm] = useState({
    id: tenant.id,
    business_name: tenant.business_name || '',
    subdomain: tenant.subdomain || '',
    business_type: tenant.business_type || '',
    email: tenant.email || '',
    phone: tenant.phone || '',
    status: tenant.status || 'trial',
    plan: tenant.plan || 'trial',
  });
  var set = function(k) { return function(v) { setForm(Object.assign({}, form, { [k]: v })); }; };
  return (
    <Modal title={'Edit: ' + tenant.business_name} onClose={onClose}>
      <FormField label="Business Name" value={form.business_name} onChange={set('business_name')} />
      <FormField label="Subdomain" value={form.subdomain} onChange={set('subdomain')} />
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
        <FormField label="Email" value={form.email} onChange={set('email')} type="email" />
        <FormField label="Phone" value={form.phone} onChange={set('phone')} />
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
        <FormField label="Business Type" value={form.business_type} onChange={set('business_type')} type="select" options={[
          { value: 'spa', label: 'Spa/Massage' }, { value: 'barbershop', label: 'Barbershop' },
          { value: 'salon', label: 'Hair Salon' }, { value: 'nails', label: 'Nail Salon' },
          { value: 'pet_grooming', label: 'Pet Grooming' }, { value: 'motorcycle', label: 'Motorcycle Repair' },
          { value: 'auto', label: 'Auto Repair' }, { value: 'smokeshop', label: 'Smoke Shop' },
          { value: 'fitness', label: 'Fitness/Gym' }, { value: 'medspa', label: 'Med Spa' },
          { value: 'other', label: 'Other' },
        ]} />
        <FormField label="Status" value={form.status} onChange={set('status')} type="select" options={[
          { value: 'active', label: 'Active' }, { value: 'trial', label: 'Trial' },
          { value: 'suspended', label: 'Suspended' }, { value: 'cancelled', label: 'Cancelled' },
        ]} />
      </div>
      <FormField label="Plan" value={form.plan} onChange={set('plan')} type="select" options={[
        { value: 'trial', label: 'Trial (Free)' }, { value: 'founding', label: 'Founding ($99/mo)' },
        { value: 'growth', label: 'Growth ($149/mo)' }, { value: 'professional', label: 'Professional ($249/mo)' },
        { value: 'enterprise', label: 'Enterprise ($399/mo)' },
      ]} />
      <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end', marginTop: 8 }}>
        <Btn outline onClick={onClose}>Cancel</Btn>
        <Btn onClick={function() { onSave(form); }}><Save size={16} /> Save Changes</Btn>
      </div>
    </Modal>
  );
}

// ════════════════════════════════════════════════════════════════
// REVENUE & MRR
// ════════════════════════════════════════════════════════════════
export function AdminRevenue() {
  var [tenants, setTenants] = useState([]);
  var [loading, setLoading] = useState(true);
  useEffect(function() {
    axios.get('/api/admin/tenants')
      .then(function(r) { setTenants(r.data.tenants || r.data.data || []); setLoading(false); })
      .catch(function() { setLoading(false); });
  }, []);
  var plans = { founding: { price: 99, color: '#ff6b2b', label: 'Founding' }, standard: { price: 199, color: '#3b82f6', label: 'Standard' }, professional: { price: 249, color: '#22c55e', label: 'Professional' }, enterprise: { price: 399, color: '#f59e0b', label: 'Enterprise' } };
  var paying = tenants.filter(function(t) { return plans[t.plan]; });
  var mrr = paying.reduce(function(s, t) { return s + (plans[t.plan]?.price || 0); }, 0);
  var arr = mrr * 12;
  var trials = tenants.filter(function(t) { return t.status === 'trial'; });
  var potentialMRR = trials.length * 99;
  var planBreakdown = {};
  paying.forEach(function(t) {
    if (!planBreakdown[t.plan]) planBreakdown[t.plan] = { count: 0, revenue: 0 };
    planBreakdown[t.plan].count++;
    planBreakdown[t.plan].revenue += plans[t.plan]?.price || 0;
  });

  return (
    <PageShell title="Revenue & MRR" icon={TrendingUp}>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 16, marginBottom: 24 }}>
        <StatCard label="MRR" value={'$' + mrr.toLocaleString()} icon={DollarSign} color="#22c55e" sub={paying.length + ' paying'} />
        <StatCard label="ARR" value={'$' + arr.toLocaleString()} icon={TrendingUp} color="#3b82f6" sub="annual projection" />
        <StatCard label="Potential MRR" value={'$' + potentialMRR.toLocaleString()} icon={ArrowUpRight} color="#f59e0b" sub={trials.length + ' trials'} />
        <StatCard label="ARPU" value={paying.length ? '$' + Math.round(mrr / paying.length) : '$0'} icon={Users} color="#a78bfa" />
      </div>
      {/* STRIPE QUICK ACTIONS */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: 16, marginBottom: 24 }}>
        <div onClick={function() { window.open('https://dashboard.stripe.com', '_blank'); }}
          style={{ background: '#1e293b', borderRadius: 12, padding: 20, border: '1px solid #334155', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 14 }}>
          <div style={{ width: 44, height: 44, borderRadius: 10, background: '#635bff22', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 22 }}>💳</div>
          <div>
            <div style={{ color: '#f1f5f9', fontWeight: 600, fontSize: 14 }}>Stripe Dashboard</div>
            <div style={{ color: '#64748b', fontSize: 12 }}>Payments, invoices, customers</div>
          </div>
        </div>
        <div onClick={function() { window.open('https://dashboard.stripe.com/test/subscriptions', '_blank'); }}
          style={{ background: '#1e293b', borderRadius: 12, padding: 20, border: '1px solid #334155', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 14 }}>
          <div style={{ width: 44, height: 44, borderRadius: 10, background: '#22c55e22', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 22 }}>🔄</div>
          <div>
            <div style={{ color: '#f1f5f9', fontWeight: 600, fontSize: 14 }}>Subscriptions</div>
            <div style={{ color: '#64748b', fontSize: 12 }}>Active, past due, canceled</div>
          </div>
        </div>
        <div onClick={function() { window.open('https://dashboard.stripe.com/test/payments', '_blank'); }}
          style={{ background: '#1e293b', borderRadius: 12, padding: 20, border: '1px solid #334155', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 14 }}>
          <div style={{ width: 44, height: 44, borderRadius: 10, background: '#3b82f622', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 22 }}>📊</div>
          <div>
            <div style={{ color: '#f1f5f9', fontWeight: 600, fontSize: 14 }}>Payments</div>
            <div style={{ color: '#64748b', fontSize: 12 }}>Transaction history</div>
          </div>
        </div>
      </div>

      {/* TENANT BILLING STATUS */}
      <div style={{ background: '#1e293b', borderRadius: 12, padding: 24, border: '1px solid #334155', marginBottom: 24 }}>
        <h3 style={{ color: '#f1f5f9', fontSize: 16, fontWeight: 600, marginBottom: 16 }}>All Businesses — Billing Status</h3>
        {tenants.sort(function(a, b) { return (plans[b.plan]?.price || 0) - (plans[a.plan]?.price || 0); }).map(function(t) {
          var p = plans[t.plan];
          var statusColor = { active: '#22c55e', trial: '#f59e0b', suspended: '#ef4444', canceled: '#64748b', past_due: '#ef4444' };
          var dl = t.trial_ends_at ? Math.ceil((new Date(t.trial_ends_at) - new Date()) / 86400000) : null;
          return (
            <div key={t.id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '12px 0', borderBottom: '1px solid #334155' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                <div style={{ width: 36, height: 36, borderRadius: 8, background: (p?.color || '#64748b') + '22', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 14, fontWeight: 800, color: p?.color || '#64748b' }}>{(t.business_name || '?')[0]}</div>
                <div>
                  <div style={{ color: '#f1f5f9', fontWeight: 600, fontSize: 14 }}>{t.business_name}</div>
                  <div style={{ color: '#64748b', fontSize: 12 }}>{t.email || t.subdomain}</div>
                </div>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                  <div style={{ width: 8, height: 8, borderRadius: '50%', background: statusColor[t.status] || '#64748b' }} />
                  <span style={{ color: '#94a3b8', fontSize: 12, textTransform: 'capitalize' }}>{t.status}{dl !== null && t.status === 'trial' ? ' (' + dl + 'd)' : ''}</span>
                </div>
                <span style={{ color: p ? '#22c55e' : '#64748b', fontWeight: p ? 700 : 400, fontSize: 14 }}>{p ? '$' + p.price + '/mo' : '$0'}</span>
                {!p && <button onClick={function() { navigator.clipboard.writeText('https://ivisionmarketing.com/app/billing'); alert('Billing link copied! Send to: ' + (t.email || t.business_name)); }}
                  style={{ background: '#ff6b2b', color: '#fff', border: 'none', padding: '4px 10px', borderRadius: 6, fontSize: 11, fontWeight: 600, cursor: 'pointer' }}>Send Checkout</button>}
                {t.stripe_customer_id && <button onClick={function() { window.open('https://dashboard.stripe.com/test/customers/' + t.stripe_customer_id, '_blank'); }}
                  style={{ background: 'transparent', color: '#635bff', border: '1px solid #635bff', padding: '4px 10px', borderRadius: 6, fontSize: 11, fontWeight: 600, cursor: 'pointer' }}>View in Stripe</button>}
              </div>
            </div>
          );
        })}
      </div>

      {/* MRR BAR */}
      <div style={{ background: '#1e293b', borderRadius: 12, padding: 24, border: '1px solid #334155', marginBottom: 24 }}>
        <h3 style={{ color: '#f1f5f9', fontSize: 16, fontWeight: 600, marginBottom: 16 }}>MRR Breakdown</h3>
        {mrr === 0 ? (<div style={{ textAlign: 'center', color: '#64748b', padding: 20 }}>No revenue yet</div>) : (
          <div>
            <div style={{ display: 'flex', borderRadius: 8, overflow: 'hidden', height: 32, marginBottom: 16 }}>
              {Object.keys(planBreakdown).map(function(plan) {
                var pct = planBreakdown[plan].revenue / mrr * 100;
                return (<div key={plan} style={{ width: pct + '%', background: plans[plan]?.color || '#64748b', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 11, fontWeight: 700, color: '#fff' }}>{pct > 15 ? Math.round(pct) + '%' : ''}</div>);
              })}
            </div>
            <div style={{ display: 'flex', gap: 20, flexWrap: 'wrap' }}>
              {Object.keys(planBreakdown).map(function(plan) {
                return (<div key={plan} style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <div style={{ width: 12, height: 12, borderRadius: 3, background: plans[plan]?.color }} />
                  <span style={{ color: '#94a3b8', fontSize: 13 }}>{plans[plan]?.label} — {planBreakdown[plan].count} x ${plans[plan]?.price} = <strong style={{ color: '#f1f5f9' }}>${planBreakdown[plan].revenue}/mo</strong></span>
                </div>);
              })}
            </div>
          </div>
        )}
      </div>
      {/* CUSTOMERS + PIPELINE */}
      <div style={{ background: '#1e293b', borderRadius: 12, padding: 24, border: '1px solid #334155' }}>
        <h3 style={{ color: '#f1f5f9', fontSize: 16, fontWeight: 600, marginBottom: 16 }}>Paying Customers</h3>
        {paying.length === 0 ? (<div style={{ color: '#64748b', textAlign: 'center', padding: 20 }}>No paying customers yet</div>) :
          paying.map(function(t) {
            var p = plans[t.plan];
            return (<div key={t.id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '14px 0', borderBottom: '1px solid #334155' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                <div style={{ width: 36, height: 36, borderRadius: 8, background: (p?.color || '#64748b') + '22', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 14, fontWeight: 800, color: p?.color }}>{(t.business_name || '?')[0]}</div>
                <div>
                  <div style={{ color: '#f1f5f9', fontWeight: 600 }}>{t.business_name}</div>
                  <div style={{ color: '#64748b', fontSize: 13 }}>{t.business_type} · {t.email}</div>
                </div>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                <Badge text={t.plan} color={p?.color || '#64748b'} />
                <span style={{ color: '#22c55e', fontWeight: 700, fontSize: 18 }}>${p?.price}/mo</span>
              </div>
            </div>);
          })
        }
        {trials.length > 0 && (
          <div style={{ marginTop: 24 }}>
            <h4 style={{ color: '#f59e0b', fontSize: 14, fontWeight: 600, marginBottom: 12 }}>Pipeline — {trials.length} Trials (${potentialMRR}/mo potential)</h4>
            {trials.map(function(t) {
              var dl = t.trial_ends_at ? Math.ceil((new Date(t.trial_ends_at) - new Date()) / 86400000) : null;
              return (<div key={t.id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '10px 0', borderBottom: '1px solid #334155' }}>
                <div><div style={{ color: '#e2e8f0', fontWeight: 600, fontSize: 14 }}>{t.business_name}</div><div style={{ color: '#64748b', fontSize: 12 }}>{t.email}</div></div>
                <Badge text={dl !== null ? (dl <= 0 ? 'Expired' : dl + ' days left') : 'Trial'} color={dl <= 3 ? '#ef4444' : '#f59e0b'} />
              </div>);
            })}
          </div>
        )}
      </div>
    </PageShell>
  );
}

// ════════════════════════════════════════════════════════════════
// LEADS & DEMOS — Full pipeline with add/edit/delete/stage
// ════════════════════════════════════════════════════════════════
var LEAD_STAGES = [
  { key: 'new', label: 'New Lead', color: '#94a3b8' },
  { key: 'contacted', label: 'Contacted', color: '#3b82f6' },
  { key: 'demo_scheduled', label: 'Demo Set', color: '#a78bfa' },
  { key: 'demo_done', label: 'Demo Done', color: '#f59e0b' },
  { key: 'negotiating', label: 'Negotiating', color: '#fb923c' },
  { key: 'won', label: 'Won', color: '#22c55e' },
  { key: 'lost', label: 'Lost', color: '#ef4444' },
];
var BIZ_TYPES = {
  spa: { label: 'Spa', color: '#ec4899' }, barbershop: { label: 'Barber', color: '#f59e0b' },
  salon: { label: 'Salon', color: '#a78bfa' }, nails: { label: 'Nails', color: '#f472b6' },
  auto: { label: 'Auto', color: '#64748b' }, motorcycle: { label: 'Moto', color: '#78716c' },
  smokeshop: { label: 'Smoke', color: '#84cc16' }, fitness: { label: 'Fitness', color: '#06b6d4' },
  medspa: { label: 'MedSpa', color: '#14b8a6' }, pet_grooming: { label: 'Pets', color: '#fb923c' },
  other: { label: 'Other', color: '#64748b' },
};
var ACT_TYPES = [
  { key: 'call', label: 'Called', icon: '\u260e\ufe0f', color: '#22c55e' },
  { key: 'email', label: 'Emailed', icon: '\u2709\ufe0f', color: '#3b82f6' },
  { key: 'sms', label: 'Texted', icon: '\ud83d\udcf1', color: '#a78bfa' },
  { key: 'meeting', label: 'Meeting', icon: '\ud83e\udd1d', color: '#f59e0b' },
  { key: 'demo', label: 'Demo', icon: '\ud83d\udcbb', color: '#06b6d4' },
  { key: 'note', label: 'Note', icon: '\ud83d\udcdd', color: '#64748b' },
];

export function AdminLeads() {
  var navigate = useNavigate();
  var [leads, setLeads] = useState([]);
  var [loading, setLoading] = useState(true);
  var [showModal, setShowModal] = useState(false);
  var [editLead, setEditLead] = useState(null);
  var [search, setSearch] = useState('');
  var [filterStage, setFilterStage] = useState('all');
  var [showImport, setShowImport] = useState(false);
  var [importResult, setImportResult] = useState(null);
  var [importing, setImporting] = useState(false);
  var [selected, setSelected] = useState([]);
  var [sortBy, setSortBy] = useState('created_at');
  var [sortDir, setSortDir] = useState('desc');
  var [showConvert, setShowConvert] = useState(null);
  var [detailLead, setDetailLead] = useState(null);
  var [activities, setActivities] = useState([]);
  var [actLoading, setActLoading] = useState(false);
  var [logText, setLogText] = useState('');
  var [logOutcome, setLogOutcome] = useState('');

  var loadLeads = async function() {
    try {
      var res = await axios.get('/api/admin/leads');
      var raw = res.data.leads || res.data || [];
      setLeads(raw.map(function(l) {
        return Object.assign({}, l, {
          business: l.business_name || l.business || '',
          contact: l.contact_name || l.contact || l.name || '',
          type: l.business_type || l.type || '',
        });
      }));
    } catch(e) { console.error('loadLeads error:', e); }
    setLoading(false);
  };
  useEffect(function() { loadLeads(); }, []);

  var openDetail = async function(lead) {
    setDetailLead(lead);
    setActLoading(true);
    setLogText(''); setLogOutcome('');
    try {
      var res = await axios.get('/api/admin/leads/' + lead.id + '/activities');
      setActivities(res.data.activities || []);
    } catch(e) { setActivities([]); }
    setActLoading(false);
  };

  var logActivity = async function(type) {
    if (!detailLead) return;
    try {
      await axios.post('/api/admin/leads/' + detailLead.id + '/activities', {
        type: type,
        description: logText,
        outcome: logOutcome,
      });
      setLogText(''); setLogOutcome('');
      var res = await axios.get('/api/admin/leads/' + detailLead.id + '/activities');
      setActivities(res.data.activities || []);
      await loadLeads();
    } catch(e) { console.error('logActivity error:', e); }
  };

  var deleteActivity = async function(actId) {
    if (!detailLead || !window.confirm('Delete this activity?')) return;
    try {
      await axios.delete('/api/admin/leads/' + detailLead.id + '/activities/' + actId);
      setActivities(function(prev) { return prev.filter(function(a) { return a.id !== actId; }); });
    } catch(e) { console.error(e); }
  };

  var saveLead = async function(lead) {
    try {
      var payload = {
        business_name: lead.business, contact_name: lead.contact,
        phone: lead.phone, email: lead.email, business_type: lead.type,
        notes: lead.notes, stage: lead.stage, source: lead.source || 'manual',
        city: lead.city || '', state: lead.state || '', website: lead.website || '',
        estimated_value: lead.estimated_value || null,
        next_followup: lead.next_followup || null, lost_reason: lead.lost_reason || '',
      };
      if (lead.id && !String(lead.id).startsWith('new')) {
        await axios.put('/api/admin/leads/' + lead.id, payload);
      } else {
        await axios.post('/api/admin/leads', payload);
      }
      await loadLeads();
    } catch(e) { console.error('saveLead error:', e); }
    setShowModal(false); setEditLead(null);
  };

  var deleteLead = async function(id) {
    if (!window.confirm('Delete this lead?')) return;
    try { await axios.delete('/api/admin/leads/' + id); await loadLeads(); if (detailLead && detailLead.id === id) setDetailLead(null); }
    catch(e) { console.error(e); }
  };

  var moveStage = async function(id, stage) {
    try { await axios.put('/api/admin/leads/' + id, { stage: stage }); await loadLeads(); }
    catch(e) { console.error(e); }
  };

  var bulkMoveStage = async function(stage) {
    try { await Promise.all(selected.map(function(id) { return axios.put('/api/admin/leads/' + id, { stage: stage }); })); setSelected([]); await loadLeads(); }
    catch(e) { console.error(e); }
  };

  var bulkDelete = async function() {
    if (!window.confirm('Delete ' + selected.length + ' leads?')) return;
    try { await axios.delete('/api/admin/leads', { data: { ids: selected } }); setSelected([]); await loadLeads(); }
    catch(e) { console.error(e); }
  };

  var toggleSelect = function(id) { setSelected(function(p) { return p.includes(id) ? p.filter(function(x) { return x !== id; }) : p.concat([id]); }); };
  var toggleSelectAll = function() { if (selected.length === filtered.length) setSelected([]); else setSelected(filtered.map(function(l) { return l.id; })); };

  var convertToTenant = async function(lead) {
    try {
      await axios.post('/api/admin/tenants', { business_name: lead.business, email: lead.email, phone: lead.phone, business_type: lead.type, plan: 'trial', status: 'trial' });
      await moveStage(lead.id, 'won');
      setShowConvert(null);
      alert('Created tenant: ' + lead.business + '!');
    } catch(e) { alert('Error: ' + (e.response?.data?.error || e.message)); }
  };

  var handleCSVImport = async function(e) {
    var file = e.target.files[0];
    if (!file) return;
    setImporting(true); setImportResult(null);
    var reader = new FileReader();
    reader.onload = async function(ev) {
      try {
        var text = ev.target.result;
        var lines = text.split('\n').filter(function(l) { return l.trim(); });
        if (lines.length < 2) { setImportResult({ error: 'CSV needs header + data rows' }); setImporting(false); return; }
        var headers = lines[0].split(',').map(function(h) { return h.trim().replace(/^["']|["']$/g, '').toLowerCase().replace(/\s+/g, '_'); });
        var leadsArr = [];
        for (var i = 1; i < lines.length; i++) {
          var vals = []; var cur = ''; var inQ = false;
          for (var c = 0; c < lines[i].length; c++) { var ch = lines[i][c]; if (ch === '"') inQ = !inQ; else if (ch === ',' && !inQ) { vals.push(cur.trim()); cur = ''; } else cur += ch; }
          vals.push(cur.trim());
          var obj = {}; headers.forEach(function(h, idx) { obj[h] = (vals[idx] || '').replace(/^["']|["']$/g, ''); });
          if (obj.business_name || obj.business || obj.name) {
            leadsArr.push({ business_name: obj.business_name || obj.business || obj.name || '', contact_name: obj.contact_name || obj.contact || obj.owner || '',
              email: obj.email || '', phone: obj.phone || obj.telephone || '', business_type: obj.business_type || obj.type || obj.industry || '',
              city: obj.city || '', state: obj.state || '', website: obj.website || obj.url || '', notes: obj.notes || '', estimated_value: obj.estimated_value || obj.value || '' });
          }
        }
        if (leadsArr.length === 0) { setImportResult({ error: 'No valid leads found' }); setImporting(false); return; }
        var res = await axios.post('/api/admin/leads/import', { leads: leadsArr });
        setImportResult(res.data.results || res.data); await loadLeads();
      } catch(err) { setImportResult({ error: err.message || 'Import failed' }); }
      setImporting(false);
    };
    reader.readAsText(file); e.target.value = '';
  };

  var handleExport = function() {
    var h = ['business_name','contact_name','email','phone','business_type','stage','source','city','state','website','notes','estimated_value','next_followup','created_at'];
    var rows = [h.join(',')];
    leads.forEach(function(l) { rows.push(h.map(function(k) { var v = String(l[k] || l[{business_name:'business',contact_name:'contact',business_type:'type'}[k]] || ''); return '"' + v.replace(/"/g, '""') + '"'; }).join(',')); });
    var b = new Blob([rows.join('\n')], { type: 'text/csv' }); var u = URL.createObjectURL(b);
    var a = document.createElement('a'); a.href = u; a.download = 'ivision_leads_' + new Date().toISOString().slice(0,10) + '.csv'; a.click(); URL.revokeObjectURL(u);
  };

  var toggleSort = function(f) { if (sortBy === f) setSortDir(sortDir === 'asc' ? 'desc' : 'asc'); else { setSortBy(f); setSortDir('desc'); } };

  var filtered = leads.filter(function(l) {
    var m = (l.business || '').toLowerCase().includes(search.toLowerCase()) || (l.contact || '').toLowerCase().includes(search.toLowerCase()) || (l.email || '').toLowerCase().includes(search.toLowerCase()) || (l.city || '').toLowerCase().includes(search.toLowerCase());
    return filterStage === 'all' ? m : m && l.stage === filterStage;
  }).sort(function(a, b) {
    var va = a[sortBy] || '', vb = b[sortBy] || '';
    if (sortBy === 'estimated_value') { va = parseFloat(va) || 0; vb = parseFloat(vb) || 0; }
    if (sortBy === 'created_at' || sortBy === 'next_followup') { va = new Date(va || 0).getTime(); vb = new Date(vb || 0).getTime(); }
    if (typeof va === 'string') { va = va.toLowerCase(); vb = vb.toLowerCase(); }
    return va < vb ? (sortDir === 'asc' ? -1 : 1) : va > vb ? (sortDir === 'asc' ? 1 : -1) : 0;
  });

  var stageCounts = {}; LEAD_STAGES.forEach(function(s) { stageCounts[s.key] = leads.filter(function(l) { return l.stage === s.key; }).length; });
  var totalPipeline = leads.filter(function(l) { return l.stage !== 'lost' && l.stage !== 'won'; }).reduce(function(s, l) { return s + (parseFloat(l.estimated_value) || 0); }, 0);
  var wonRevenue = leads.filter(function(l) { return l.stage === 'won'; }).reduce(function(s, l) { return s + (parseFloat(l.estimated_value) || 0); }, 0);
  var weekAgo = new Date(); weekAgo.setDate(weekAgo.getDate() - 7);
  var newThisWeek = leads.filter(function(l) { return new Date(l.created_at) > weekAgo; }).length;
  var conversionRate = leads.length > 0 ? Math.round((stageCounts['won'] || 0) / leads.length * 100) : 0;
  var today = new Date().toISOString().slice(0, 10);
  var overdue = leads.filter(function(l) { return l.next_followup && l.next_followup < today && l.stage !== 'won' && l.stage !== 'lost'; });
  var dueToday = leads.filter(function(l) { return l.next_followup && l.next_followup === today; });
  var timeAgo = function(d) { if (!d) return ''; var m = Math.floor((Date.now() - new Date(d).getTime()) / 60000); if (m < 1) return 'just now'; if (m < 60) return m + 'm ago'; if (m < 1440) return Math.floor(m / 60) + 'h ago'; return Math.floor(m / 1440) + 'd ago'; };

  return (
    <PageShell title="Leads & Demos" icon={UserPlus} action={
      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
        <Btn outline onClick={function() { setShowImport(!showImport); }}><Upload size={16} /> Import CSV</Btn>
        <Btn outline onClick={handleExport} disabled={leads.length === 0}><Download size={16} /> Export</Btn>
        <Btn onClick={function() { setEditLead({ business: '', contact: '', email: '', phone: '', type: '', stage: 'new', notes: '', source: '', city: '', state: '', website: '', estimated_value: '', next_followup: '', lost_reason: '' }); setShowModal(true); }}><Plus size={16} /> Add Lead</Btn>
      </div>
    }>
      {/* STATS */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', gap: 12, marginBottom: 20 }}>
        <StatCard label="Total Leads" value={leads.length} icon={Users} color="#a78bfa" />
        <StatCard label="Pipeline Value" value={'$' + totalPipeline.toLocaleString() + '/mo'} icon={DollarSign} color="#3b82f6" />
        <StatCard label="Won Revenue" value={'$' + wonRevenue.toLocaleString() + '/mo'} icon={CheckCircle} color="#22c55e" />
        <StatCard label="New This Week" value={newThisWeek} icon={ArrowUpRight} color="#f59e0b" />
        <StatCard label="Conversion" value={conversionRate + '%'} icon={TrendingUp} color="#06b6d4" />
      </div>

      {/* FOLLOW-UP ALERTS */}
      {(overdue.length > 0 || dueToday.length > 0) && (
        <div style={{ background: '#1e293b', borderRadius: 12, border: '1px solid #f59e0b44', padding: 16, marginBottom: 20 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
            <Clock size={16} style={{ color: '#f59e0b' }} />
            <span style={{ color: '#f59e0b', fontWeight: 600, fontSize: 14 }}>Follow-up Alerts</span>
          </div>
          {overdue.map(function(l) { return (<div key={'o'+l.id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '6px 0' }}>
            <span style={{ color: '#ef4444', fontSize: 13 }}>{'\u26a0\ufe0f'} <strong>{l.business}</strong> — overdue since {l.next_followup}</span>
            <Btn small onClick={function() { openDetail(l); }}>View</Btn>
          </div>); })}
          {dueToday.map(function(l) { return (<div key={'t'+l.id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '6px 0' }}>
            <span style={{ color: '#f59e0b', fontSize: 13 }}>{'\ud83d\udcc5'} <strong>{l.business}</strong> — follow up today</span>
            <Btn small onClick={function() { openDetail(l); }}>View</Btn>
          </div>); })}
        </div>
      )}

      {/* CSV IMPORT */}
      {showImport && (
        <div style={{ background: '#1e293b', borderRadius: 12, border: '1px solid #334155', padding: 20, marginBottom: 20 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
            <h3 style={{ color: '#f1f5f9', fontSize: 16, fontWeight: 600, margin: 0 }}>Import Leads from CSV</h3>
            <button onClick={function() { setShowImport(false); setImportResult(null); }} style={{ background: 'none', border: 'none', color: '#64748b', cursor: 'pointer' }}><X size={18} /></button>
          </div>
          <div style={{ color: '#94a3b8', fontSize: 13, marginBottom: 12 }}>CSV headers: <span style={{ color: '#a78bfa' }}>business_name</span> (required), contact_name, email, phone, business_type, city, state, website, notes, estimated_value</div>
          <label style={{ display: 'inline-flex', alignItems: 'center', gap: 8, padding: '10px 20px', background: importing ? '#334155' : '#a78bfa22', border: '1px solid #a78bfa55', borderRadius: 8, cursor: importing ? 'wait' : 'pointer', color: '#a78bfa', fontWeight: 600, fontSize: 14 }}>
            <Upload size={16} /> {importing ? 'Importing...' : 'Choose CSV File'}
            <input type="file" accept=".csv,.txt" onChange={handleCSVImport} style={{ display: 'none' }} disabled={importing} />
          </label>
          {importResult && (<div style={{ marginTop: 12, padding: 12, borderRadius: 8, background: importResult.error ? '#ef444422' : '#22c55e22', border: '1px solid ' + (importResult.error ? '#ef4444' : '#22c55e') }}>
            {importResult.error ? <div style={{ color: '#ef4444' }}>{importResult.error}</div> :
              <div style={{ color: '#22c55e' }}>Imported <strong>{importResult.inserted}</strong> leads{importResult.duplicates > 0 && <span style={{ color: '#f59e0b' }}> &middot; {importResult.duplicates} dupes</span>}{importResult.skipped > 0 && <span style={{ color: '#ef4444' }}> &middot; {importResult.skipped} errors</span>}</div>}
          </div>)}
        </div>
      )}

      {/* LAYOUT: LIST + DETAIL PANEL */}
      <div style={{ display: 'flex', gap: 16 }}>
        {/* LEFT: LEADS LIST */}
        <div style={{ flex: detailLead ? '0 0 55%' : '1 1 100%', minWidth: 0, transition: 'flex 0.2s' }}>
          {/* STAGE PILLS */}
          <div style={{ display: 'flex', gap: 6, marginBottom: 16, overflowX: 'auto', paddingBottom: 4 }}>
            <div onClick={function() { setFilterStage('all'); }} style={{ flex: '1 0 auto', minWidth: 60, padding: '8px 10px', borderRadius: 8, background: filterStage === 'all' ? '#a78bfa33' : '#1e293b', border: '1px solid ' + (filterStage === 'all' ? '#a78bfa' : '#334155'), cursor: 'pointer', textAlign: 'center' }}>
              <div style={{ fontSize: 18, fontWeight: 700, color: '#a78bfa' }}>{leads.length}</div>
              <div style={{ fontSize: 9, color: '#94a3b8', fontWeight: 600 }}>All</div>
            </div>
            {LEAD_STAGES.map(function(s) { return (<div key={s.key} onClick={function() { setFilterStage(filterStage === s.key ? 'all' : s.key); }} style={{ flex: '1 0 auto', minWidth: 60, padding: '8px 10px', borderRadius: 8, background: filterStage === s.key ? s.color + '33' : '#1e293b', border: '1px solid ' + (filterStage === s.key ? s.color : '#334155'), cursor: 'pointer', textAlign: 'center' }}>
              <div style={{ fontSize: 18, fontWeight: 700, color: s.color }}>{stageCounts[s.key] || 0}</div>
              <div style={{ fontSize: 9, color: '#94a3b8', fontWeight: 600 }}>{s.label}</div>
            </div>); })}
          </div>

          {/* SEARCH + BULK */}
          <div style={{ display: 'flex', gap: 8, marginBottom: 12, alignItems: 'center', flexWrap: 'wrap' }}>
            <div style={{ flex: 1, minWidth: 180 }}><SearchBar value={search} onChange={setSearch} placeholder="Search leads..." /></div>
            {selected.length > 0 && (<div style={{ display: 'flex', gap: 4, alignItems: 'center' }}>
              <span style={{ color: '#a78bfa', fontSize: 12, fontWeight: 600 }}>{selected.length} sel</span>
              <select onChange={function(e) { if (e.target.value) bulkMoveStage(e.target.value); e.target.value = ''; }} style={{ background: '#0f172a', border: '1px solid #334155', borderRadius: 6, color: '#e2e8f0', fontSize: 11, padding: '3px 6px' }}>
                <option value="">Move to...</option>
                {LEAD_STAGES.map(function(s) { return <option key={s.key} value={s.key}>{s.label}</option>; })}
              </select>
              <Btn small outline color="#ef4444" onClick={bulkDelete}><Trash2 size={12} /></Btn>
              <Btn small outline onClick={function() { setSelected([]); }}>Clear</Btn>
            </div>)}
            {filterStage !== 'all' && <Btn small outline onClick={function() { setFilterStage('all'); }}>Clear Filter</Btn>}
          </div>

          {/* COLUMN HEADERS */}
          <div style={{ display: 'flex', gap: 8, padding: '6px 12px', color: '#475569', fontSize: 10, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.5px' }}>
            <span style={{ width: 24 }}><input type="checkbox" checked={selected.length === filtered.length && filtered.length > 0} onChange={toggleSelectAll} /></span>
            <span onClick={function() { toggleSort('business_name'); }} style={{ flex: 2, cursor: 'pointer' }}>Business {sortBy === 'business_name' ? (sortDir === 'asc' ? '\u2191' : '\u2193') : ''}</span>
            <span onClick={function() { toggleSort('estimated_value'); }} style={{ width: 70, cursor: 'pointer', textAlign: 'right' }}>Value {sortBy === 'estimated_value' ? (sortDir === 'asc' ? '\u2191' : '\u2193') : ''}</span>
            <span onClick={function() { toggleSort('created_at'); }} style={{ width: 60, cursor: 'pointer', textAlign: 'right' }}>Added {sortBy === 'created_at' ? (sortDir === 'asc' ? '\u2191' : '\u2193') : ''}</span>
            <span style={{ width: 100, textAlign: 'right' }}>Actions</span>
          </div>

          {/* LEADS */}
          <div style={{ background: '#1e293b', borderRadius: 12, border: '1px solid #334155', maxHeight: detailLead ? '60vh' : 'none', overflowY: detailLead ? 'auto' : 'visible' }}>
            {loading ? <div style={{ padding: 40, textAlign: 'center', color: '#64748b' }}>Loading...</div> :
             filtered.length === 0 ? <div style={{ padding: 40, textAlign: 'center', color: '#64748b' }}>{leads.length === 0 ? 'No leads yet. Add or import to start.' : 'No matches.'}</div> :
             filtered.map(function(l, idx) {
              var stage = LEAD_STAGES.find(function(s) { return s.key === l.stage; }) || LEAD_STAGES[0];
              var bt = BIZ_TYPES[l.type] || BIZ_TYPES.other;
              var isOverdue = l.next_followup && l.next_followup < today && l.stage !== 'won' && l.stage !== 'lost';
              var isActive = detailLead && detailLead.id === l.id;
              return (<div key={l.id} onClick={function() { openDetail(l); }} style={{
                padding: '10px 12px', borderBottom: idx < filtered.length - 1 ? '1px solid #33415566' : 'none',
                display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer',
                background: isActive ? '#a78bfa15' : selected.includes(l.id) ? '#a78bfa08' : 'transparent',
                borderLeft: isOverdue ? '3px solid #ef4444' : isActive ? '3px solid #a78bfa' : '3px solid transparent',
              }}>
                <input type="checkbox" checked={selected.includes(l.id)} onChange={function(e) { e.stopPropagation(); toggleSelect(l.id); }} onClick={function(e) { e.stopPropagation(); }} style={{ cursor: 'pointer' }} />
                <div style={{ flex: 2, minWidth: 0 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 5, flexWrap: 'wrap' }}>
                    <span style={{ color: '#f1f5f9', fontWeight: 600, fontSize: 13, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{l.business || 'Unnamed'}</span>
                    <Badge text={stage.label} color={stage.color} />
                    {l.type && <span style={{ fontSize: 9, padding: '1px 5px', borderRadius: 3, background: bt.color + '22', color: bt.color, fontWeight: 600 }}>{bt.label}</span>}
                  </div>
                  <div style={{ color: '#64748b', fontSize: 11, marginTop: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{l.contact}{l.email ? ' \u00b7 ' + l.email : ''}{l.city ? ' \u00b7 ' + l.city : ''}</div>
                </div>
                <div style={{ width: 70, textAlign: 'right' }}>
                  {l.estimated_value ? <span style={{ color: '#22c55e', fontWeight: 700, fontSize: 13 }}>${parseFloat(l.estimated_value).toFixed(0)}</span> : <span style={{ color: '#334155' }}>—</span>}
                </div>
                <div style={{ width: 60, textAlign: 'right', color: '#64748b', fontSize: 10 }}>{timeAgo(l.created_at)}</div>
                <div style={{ width: 100, display: 'flex', gap: 3, justifyContent: 'flex-end' }} onClick={function(e) { e.stopPropagation(); }}>
                  <select value={l.stage} onChange={function(e) { moveStage(l.id, e.target.value); }} style={{ background: '#0f172a', border: '1px solid #334155', borderRadius: 4, color: '#e2e8f0', fontSize: 10, padding: '2px 4px', cursor: 'pointer', maxWidth: 55 }}>
                    {LEAD_STAGES.map(function(s) { return <option key={s.key} value={s.key}>{s.label}</option>; })}
                  </select>
                  <button onClick={function() { setEditLead(Object.assign({}, l)); setShowModal(true); }} style={{ background: '#33415522', color: '#94a3b8', border: 'none', borderRadius: 4, padding: '4px 5px', cursor: 'pointer' }}><Edit2 size={11} /></button>
                  <button onClick={function() { deleteLead(l.id); }} style={{ background: '#ef444422', color: '#ef4444', border: 'none', borderRadius: 4, padding: '4px 5px', cursor: 'pointer' }}><Trash2 size={11} /></button>
                </div>
              </div>);
            })}
          </div>
        </div>

        {/* RIGHT: DETAIL PANEL */}
        {detailLead && (
          <div style={{ flex: '0 0 43%', background: '#1e293b', borderRadius: 12, border: '1px solid #334155', padding: 20, maxHeight: '80vh', overflowY: 'auto', position: 'sticky', top: 80 }}>
            {/* Header */}
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start', marginBottom: 16 }}>
              <div>
                <h2 style={{ color: '#f1f5f9', fontSize: 18, fontWeight: 700, margin: 0 }}>{detailLead.business}</h2>
                <div style={{ color: '#64748b', fontSize: 13, marginTop: 2 }}>{detailLead.contact}</div>
              </div>
              <button onClick={function() { setDetailLead(null); }} style={{ background: 'none', border: 'none', color: '#64748b', cursor: 'pointer', padding: 4 }}><X size={18} /></button>
            </div>

            {/* Contact Info */}
            <div style={{ display: 'flex', gap: 8, marginBottom: 16, flexWrap: 'wrap' }}>
              {detailLead.phone && <a href={'tel:' + detailLead.phone} style={{ display: 'inline-flex', alignItems: 'center', gap: 6, padding: '8px 14px', background: '#22c55e15', border: '1px solid #22c55e44', borderRadius: 8, color: '#22c55e', fontSize: 13, fontWeight: 600, textDecoration: 'none' }}><Phone size={14} /> {detailLead.phone}</a>}
              {detailLead.email && <a href={'mailto:' + detailLead.email} style={{ display: 'inline-flex', alignItems: 'center', gap: 6, padding: '8px 14px', background: '#3b82f615', border: '1px solid #3b82f644', borderRadius: 8, color: '#3b82f6', fontSize: 13, fontWeight: 600, textDecoration: 'none' }}><Mail size={14} /> {detailLead.email}</a>}
              {detailLead.website && <a href={detailLead.website.startsWith('http') ? detailLead.website : 'https://' + detailLead.website} target="_blank" rel="noreferrer" style={{ display: 'inline-flex', alignItems: 'center', gap: 6, padding: '8px 14px', background: '#a78bfa15', border: '1px solid #a78bfa44', borderRadius: 8, color: '#a78bfa', fontSize: 13, fontWeight: 600, textDecoration: 'none' }}><ExternalLink size={14} /> Website</a>}
            </div>

            {/* Details Grid */}
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8, marginBottom: 16, fontSize: 12 }}>
              <div style={{ color: '#64748b' }}>Stage: <span style={{ color: (LEAD_STAGES.find(function(s){return s.key===detailLead.stage;}) || {}).color || '#94a3b8', fontWeight: 600 }}>{(LEAD_STAGES.find(function(s){return s.key===detailLead.stage;}) || {}).label || detailLead.stage}</span></div>
              <div style={{ color: '#64748b' }}>Type: <span style={{ color: '#e2e8f0' }}>{(BIZ_TYPES[detailLead.type] || {}).label || detailLead.type || '—'}</span></div>
              <div style={{ color: '#64748b' }}>Value: <span style={{ color: '#22c55e', fontWeight: 600 }}>{detailLead.estimated_value ? '$' + detailLead.estimated_value + '/mo' : '—'}</span></div>
              <div style={{ color: '#64748b' }}>Source: <span style={{ color: '#e2e8f0' }}>{detailLead.source || '—'}</span></div>
              <div style={{ color: '#64748b' }}>Location: <span style={{ color: '#e2e8f0' }}>{detailLead.city ? detailLead.city + (detailLead.state ? ', ' + detailLead.state : '') : '—'}</span></div>
              <div style={{ color: '#64748b' }}>Follow-up: <span style={{ color: detailLead.next_followup && detailLead.next_followup < today ? '#ef4444' : '#e2e8f0' }}>{detailLead.next_followup || '—'}</span></div>
            </div>
            {detailLead.notes && <div style={{ background: '#0f172a', padding: 10, borderRadius: 8, fontSize: 12, color: '#94a3b8', marginBottom: 16, fontStyle: 'italic' }}>{detailLead.notes}</div>}

            {/* Quick Actions */}
            <div style={{ display: 'flex', gap: 8, marginBottom: 16, flexWrap: 'wrap' }}>
              <Btn small onClick={function() { setEditLead(Object.assign({}, detailLead)); setShowModal(true); }}><Edit2 size={14} /> Edit</Btn>
              {(detailLead.stage === 'negotiating' || detailLead.stage === 'demo_done') && <Btn small color="#22c55e" onClick={function() { setShowConvert(detailLead); }}><ArrowRight size={14} /> Convert</Btn>}
            </div>

            {/* LOG ACTIVITY */}
            <div style={{ background: '#0f172a', borderRadius: 10, padding: 14, marginBottom: 16, border: '1px solid #334155' }}>
              <div style={{ color: '#f1f5f9', fontSize: 13, fontWeight: 600, marginBottom: 8 }}>Log Outreach</div>
              <textarea value={logText} onChange={function(e) { setLogText(e.target.value); }} placeholder="What happened? (optional)" style={{ width: '100%', padding: 8, background: '#1e293b', border: '1px solid #334155', borderRadius: 6, color: '#e2e8f0', fontSize: 12, resize: 'vertical', minHeight: 50, marginBottom: 6 }} />
              <input value={logOutcome} onChange={function(e) { setLogOutcome(e.target.value); }} placeholder="Outcome (e.g. Left voicemail, Booked demo)" style={{ width: '100%', padding: 8, background: '#1e293b', border: '1px solid #334155', borderRadius: 6, color: '#e2e8f0', fontSize: 12, marginBottom: 8 }} />
              <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                {ACT_TYPES.map(function(a) {
                  return <button key={a.key} onClick={function() { logActivity(a.key); }} style={{ padding: '6px 12px', background: a.color + '18', border: '1px solid ' + a.color + '44', borderRadius: 6, color: a.color, fontSize: 12, fontWeight: 600, cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: 4 }}>{a.icon} {a.label}</button>;
                })}
              </div>
            </div>

            {/* ACTIVITY TIMELINE */}
            <div style={{ color: '#f1f5f9', fontSize: 13, fontWeight: 600, marginBottom: 8 }}>
              Activity Log <span style={{ color: '#64748b', fontWeight: 400 }}>({activities.length} entries)</span>
            </div>
            {actLoading ? <div style={{ color: '#64748b', fontSize: 12 }}>Loading...</div> :
             activities.length === 0 ? <div style={{ color: '#475569', fontSize: 12, fontStyle: 'italic' }}>No outreach logged yet. Use the buttons above to log your first contact.</div> :
             <div style={{ position: 'relative', paddingLeft: 20 }}>
               <div style={{ position: 'absolute', left: 6, top: 0, bottom: 0, width: 2, background: '#334155' }} />
               {activities.map(function(a, idx) {
                 var at = ACT_TYPES.find(function(t) { return t.key === a.type; }) || ACT_TYPES[5];
                 var reachNum = activities.length - idx;
                 return (<div key={a.id} style={{ position: 'relative', marginBottom: 12, paddingLeft: 12 }}>
                   <div style={{ position: 'absolute', left: -14, top: 4, width: 14, height: 14, borderRadius: '50%', background: at.color + '33', border: '2px solid ' + at.color, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 8 }}>{at.icon}</div>
                   <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start' }}>
                     <div>
                       <span style={{ color: at.color, fontWeight: 600, fontSize: 12 }}>Reach Out #{reachNum} — {at.label}</span>
                       <span style={{ color: '#475569', fontSize: 10, marginLeft: 8 }}>{timeAgo(a.created_at)}</span>
                     </div>
                     <button onClick={function() { deleteActivity(a.id); }} style={{ background: 'none', border: 'none', color: '#475569', cursor: 'pointer', padding: 2 }}><Trash2 size={10} /></button>
                   </div>
                   {a.description && <div style={{ color: '#94a3b8', fontSize: 12, marginTop: 2 }}>{a.description}</div>}
                   {a.outcome && <div style={{ color: '#22c55e', fontSize: 11, marginTop: 2 }}>Outcome: {a.outcome}</div>}
                 </div>);
               })}
             </div>}
          </div>
        )}
      </div>

      {/* CONVERT MODAL */}
      {showConvert && (
        <Modal title="Convert Lead to Tenant" onClose={function() { setShowConvert(null); }}>
          <div style={{ color: '#94a3b8', marginBottom: 16 }}>Create a business account for <strong style={{ color: '#f1f5f9' }}>{showConvert.business}</strong>? 14-day free trial.</div>
          <div style={{ background: '#0f172a', padding: 12, borderRadius: 8, fontSize: 13, color: '#64748b', marginBottom: 16 }}>
            <div>{'\u2709\ufe0f'} {showConvert.email || 'No email'}</div>
            <div>{'\ud83d\udcf1'} {showConvert.phone || 'No phone'}</div>
            <div>{'\ud83c\udfe2'} {(BIZ_TYPES[showConvert.type] || {}).label || showConvert.type || 'Unknown'}</div>
          </div>
          <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
            <Btn outline onClick={function() { setShowConvert(null); }}>Cancel</Btn>
            <Btn color="#22c55e" onClick={function() { convertToTenant(showConvert); }}><ArrowRight size={16} /> Create Tenant</Btn>
          </div>
        </Modal>
      )}

      {showModal && editLead && <LeadModal lead={editLead} onSave={saveLead} onClose={function() { setShowModal(false); setEditLead(null); }} />}
    </PageShell>
  );
}

function LeadModal({ lead, onSave, onClose }) {
  var [form, setForm] = useState(lead);
  var set = function(k) { return function(v) { setForm(Object.assign({}, form, { [k]: v })); }; };
  return (
    <Modal title={lead.id ? 'Edit Lead' : 'Add Lead'} onClose={onClose}>
      <FormField label="Business Name" value={form.business} onChange={set('business')} placeholder="e.g. Dave's Barbershop" />
      <FormField label="Contact Name" value={form.contact} onChange={set('contact')} placeholder="Dave Smith" />
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
        <FormField label="Email" value={form.email} onChange={set('email')} type="email" placeholder="dave@email.com" />
        <FormField label="Phone" value={form.phone} onChange={set('phone')} placeholder="702-555-0100" />
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
        <FormField label="City" value={form.city || ''} onChange={set('city')} placeholder="Las Vegas" />
        <FormField label="State" value={form.state || ''} onChange={set('state')} placeholder="NV" />
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
        <FormField label="Business Type" value={form.type} onChange={set('type')} type="select" options={[
          { value: '', label: 'Select...' }, { value: 'spa', label: 'Spa/Massage' }, { value: 'barbershop', label: 'Barbershop' },
          { value: 'salon', label: 'Hair Salon' }, { value: 'nails', label: 'Nail Salon' }, { value: 'pet_grooming', label: 'Pet Grooming' },
          { value: 'motorcycle', label: 'Motorcycle Repair' }, { value: 'auto', label: 'Auto Repair' }, { value: 'smokeshop', label: 'Smoke Shop' },
          { value: 'fitness', label: 'Fitness/Gym' }, { value: 'medspa', label: 'Med Spa' }, { value: 'other', label: 'Other' },
        ]} />
        <FormField label="Source" value={form.source} onChange={set('source')} type="select" options={[
          { value: '', label: 'Select...' }, { value: 'referral', label: 'Referral' }, { value: 'google', label: 'Google' },
          { value: 'social', label: 'Social Media' }, { value: 'cold_outreach', label: 'Cold Outreach' },
          { value: 'csv_import', label: 'CSV Import' }, { value: 'landing_page', label: 'Landing Page' },
          { value: 'inbound', label: 'Website' }, { value: 'yelp', label: 'Yelp' }, { value: 'other', label: 'Other' },
        ]} />
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
        <FormField label="Est. Monthly Value ($)" value={form.estimated_value || ''} onChange={set('estimated_value')} placeholder="99" />
        <div>
          <label style={{ display: 'block', fontSize: 13, color: '#94a3b8', marginBottom: 4, fontWeight: 600 }}>Next Follow-up</label>
          <input type="date" value={form.next_followup || ''} onChange={function(e) { set('next_followup')(e.target.value); }}
            style={{ width: '100%', padding: '10px 12px', background: '#0f172a', border: '1px solid #334155', borderRadius: 8, color: '#e2e8f0', fontSize: 14 }} />
        </div>
      </div>
      <FormField label="Website" value={form.website || ''} onChange={set('website')} placeholder="www.example.com" />
      <FormField label="Stage" value={form.stage} onChange={set('stage')} type="select" options={LEAD_STAGES.map(function(s) { return { value: s.key, label: s.label }; })} />
      <FormField label="Notes" value={form.notes} onChange={set('notes')} type="textarea" placeholder="Demo notes, follow-ups, meeting details..." />
      {form.stage === 'lost' && <FormField label="Lost Reason" value={form.lost_reason || ''} onChange={set('lost_reason')} placeholder="Why was this lead lost?" />}
      <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end', marginTop: 8 }}>
        <Btn outline onClick={onClose}>Cancel</Btn>
        <Btn onClick={function() { onSave(form); }}><Save size={16} /> {lead.id ? 'Save' : 'Add Lead'}</Btn>
      </div>
    </Modal>
  );
}

// ════════════════════════════════════════════════════════════════
// ONBOARDING
// ════════════════════════════════════════════════════════════════
export function AdminOnboarding() {
  var [tenants, setTenants] = useState([]);
  var { isSuperAdmin } = useTenant();
  var [loading, setLoading] = useState(true);
  var [filter, setFilter] = useState('all');
  var [detail, setDetail] = useState(null);
  var [notes, setNotes] = useState([]);
  var [noteText, setNoteText] = useState('');
  var [notesLoading, setNotesLoading] = useState(false);

  var loadTenants = async function() {
    try {
      var res = await axios.get('/api/admin/tenants/enriched');
      setTenants(res.data.tenants || []);
    } catch(e) {
      try {
        var fallback = await axios.get('/api/admin/tenants');
        var raw = fallback.data.tenants || fallback.data.data || [];
        setTenants(raw.map(function(t) {
          var steps = { hasName: !!(t.business_name && t.business_name.length > 2), hasStaff: false, hasServices: false, hasCustomers: false, hasBookings: false };
          return Object.assign({}, t, { setupScore: steps.hasName ? 20 : 0, setupSteps: steps, stepsComplete: steps.hasName ? 1 : 0, staffCount: 0, serviceCount: 0, customerCount: 0, bookingCount: 0 });
        }));
      } catch(e2) { console.error(e2); }
    }
    setLoading(false);
  };
  useEffect(function() { if (isSuperAdmin) loadTenants(); }, [isSuperAdmin]);

  var trials = tenants.filter(function(t) { return t.status === 'trial' || t.plan === 'trial'; });
  var active = tenants.filter(function(t) { return t.plan !== 'trial' && t.status === 'active'; });
  var stalled = trials.filter(function(t) {
    var days = t.created_at ? Math.floor((Date.now() - new Date(t.created_at).getTime()) / 86400000) : 0;
    return days >= 5 && (t.setupScore || 0) < 40;
  });
  var onTrack = trials.filter(function(t) { return (t.setupScore || 0) >= 60; });
  var needHelp = trials.filter(function(t) { return (t.setupScore || 0) > 0 && (t.setupScore || 0) < 60 && !stalled.includes(t); });
  var notStarted = trials.filter(function(t) { return (t.setupScore || 0) === 0; });

  var displayed = filter === 'all' ? trials : filter === 'stalled' ? stalled : filter === 'onTrack' ? onTrack : filter === 'needHelp' ? needHelp : filter === 'notStarted' ? notStarted : filter === 'active' ? active : trials;

  var loginAs = function(t) { localStorage.setItem('sa_tenant_override', String(t.id)); window.location.href = '/app/dashboard'; };

  var extendTrial = async function(t) {
    try {
      await axios.post('/api/admin/tenants/' + t.id + '/extend-trial', { days: 14 });
      alert('Extended ' + t.business_name + ' by 14 days');
      await loadTenants();
    } catch(e) { alert('Error: ' + (e.response?.data?.error || e.message)); }
  };

  var upgradePlan = async function(t, plan) {
    try {
      await axios.put('/api/admin/tenants/' + t.id, { plan: plan, status: 'active' });
      alert(t.business_name + ' upgraded to ' + plan + '!');
      await loadTenants();
    } catch(e) { alert('Error: ' + (e.response?.data?.error || e.message)); }
  };

  var openDetail = async function(t) {
    setDetail(t);
    setNotesLoading(true);
    setNoteText('');
    try {
      var res = await axios.get('/api/admin/tenants/' + t.id + '/notes');
      setNotes(res.data.notes || []);
    } catch(e) { setNotes([]); }
    setNotesLoading(false);
  };

  var addNote = async function() {
    if (!detail || !noteText.trim()) return;
    try {
      await axios.post('/api/admin/tenants/' + detail.id + '/notes', { content: noteText });
      setNoteText('');
      var res = await axios.get('/api/admin/tenants/' + detail.id + '/notes');
      setNotes(res.data.notes || []);
    } catch(e) { console.error(e); }
  };

  var checklist = ['hasName', 'hasStaff', 'hasServices', 'hasCustomers', 'hasBookings'];
  var checkLabels = { hasName: 'Business Name', hasStaff: 'Staff Added', hasServices: 'Services Created', hasCustomers: 'Customers Added', hasBookings: 'First Booking' };
  var checkIcons = { hasName: '\ud83c\udfe2', hasStaff: '\ud83d\udc65', hasServices: '\u2699\ufe0f', hasCustomers: '\ud83d\udccc', hasBookings: '\ud83d\udcc5' };

  var timeAgo = function(d) { if (!d) return ''; var m = Math.floor((Date.now() - new Date(d).getTime()) / 60000); if (m < 60) return m + 'm ago'; if (m < 1440) return Math.floor(m / 60) + 'h ago'; return Math.floor(m / 1440) + 'd ago'; };

  var avgScore = trials.length > 0 ? Math.round(trials.reduce(function(s, t) { return s + (t.setupScore || 0); }, 0) / trials.length) : 0;
  var conversionRate = tenants.length > 0 ? Math.round(active.length / tenants.length * 100) : 0;

  return (
    <PageShell title="Onboarding" icon={Target}>
      {/* STATS */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(130px, 1fr))', gap: 12, marginBottom: 20 }}>
        <StatCard label="Trials" value={trials.length} icon={Target} color="#a78bfa" onClick={function() { setFilter('all'); }} />
        <StatCard label="On Track" value={onTrack.length} icon={CheckCircle} color="#22c55e" onClick={function() { setFilter('onTrack'); }} />
        <StatCard label="Need Help" value={needHelp.length} icon={AlertCircle} color="#f59e0b" onClick={function() { setFilter('needHelp'); }} />
        <StatCard label="Stalled 5d+" value={stalled.length} icon={Clock} color="#ef4444" onClick={function() { setFilter('stalled'); }} />
        <StatCard label="Avg Score" value={avgScore + '%'} icon={TrendingUp} color="#3b82f6" />
        <StatCard label="Converted" value={conversionRate + '%'} icon={ArrowUpRight} color="#06b6d4" onClick={function() { setFilter('active'); }} />
      </div>

      {/* STALLED ALERT */}
      {stalled.length > 0 && filter === 'all' && (
        <div style={{ background: '#ef444412', border: '1px solid #ef444433', borderRadius: 12, padding: 16, marginBottom: 20 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10 }}>
            <AlertCircle size={16} style={{ color: '#ef4444' }} />
            <span style={{ color: '#ef4444', fontWeight: 600, fontSize: 14 }}>{stalled.length} Stalled — signed up 5+ days, under 40% setup</span>
          </div>
          {stalled.slice(0, 3).map(function(t) {
            var days = Math.floor((Date.now() - new Date(t.created_at).getTime()) / 86400000);
            return (<div key={'s' + t.id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '5px 0' }}>
              <span style={{ color: '#f1f5f9', fontSize: 13 }}><strong>{t.business_name}</strong> — {days}d ago, {t.setupScore}% setup</span>
              <div style={{ display: 'flex', gap: 6 }}>
                <Btn small onClick={function() { openDetail(t); }}>View</Btn>
                <Btn small outline onClick={function() { loginAs(t); }}>Login As</Btn>
              </div>
            </div>);
          })}
          {stalled.length > 3 && <div style={{ color: '#64748b', fontSize: 12, marginTop: 4 }}>+ {stalled.length - 3} more</div>}
        </div>
      )}

      {/* FILTER TABS */}
      <div style={{ display: 'flex', gap: 6, marginBottom: 16, flexWrap: 'wrap' }}>
        {[{k:'all',l:'All Trials',n:trials.length},{k:'onTrack',l:'On Track',n:onTrack.length},{k:'needHelp',l:'Need Help',n:needHelp.length},{k:'stalled',l:'Stalled',n:stalled.length},{k:'notStarted',l:'Not Started',n:notStarted.length},{k:'active',l:'Converted',n:active.length}].map(function(f) {
          return <Btn key={f.k} small outline={filter !== f.k} onClick={function() { setFilter(f.k); }}>{f.l} ({f.n})</Btn>;
        })}
      </div>

      {/* LAYOUT: LIST + DETAIL */}
      <div style={{ display: 'flex', gap: 16 }}>
        {/* LEFT: TENANT LIST */}
        <div style={{ flex: detail ? '0 0 55%' : '1 1 100%', minWidth: 0 }}>
          <div style={{ background: '#1e293b', borderRadius: 12, border: '1px solid #334155' }}>
            {loading ? <div style={{ padding: 40, textAlign: 'center', color: '#64748b' }}>Loading...</div> :
             displayed.length === 0 ? <div style={{ padding: 40, textAlign: 'center', color: '#64748b' }}>No businesses in this category.</div> :
             displayed.map(function(t, idx) {
              var dl = t.trial_ends_at ? Math.ceil((new Date(t.trial_ends_at) - new Date()) / 86400000) : null;
              var days = t.created_at ? Math.floor((Date.now() - new Date(t.created_at).getTime()) / 86400000) : 0;
              var sc = (t.setupScore || 0) >= 80 ? '#22c55e' : (t.setupScore || 0) >= 40 ? '#f59e0b' : '#ef4444';
              var isActive = detail && detail.id === t.id;
              return (<div key={t.id} onClick={function() { openDetail(t); }} style={{
                padding: '14px 16px', borderBottom: idx < displayed.length - 1 ? '1px solid #334155' : 'none',
                cursor: 'pointer', background: isActive ? '#a78bfa12' : 'transparent',
                borderLeft: isActive ? '3px solid #a78bfa' : '3px solid transparent',
              }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start', marginBottom: 8 }}>
                  <div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                      <span style={{ color: '#f1f5f9', fontWeight: 600, fontSize: 14 }}>{t.business_name}</span>
                      {t.business_type && <Badge text={t.business_type} color="#64748b" />}
                      <Badge text={dl !== null ? (dl <= 0 ? 'EXPIRED' : dl + 'd left') : (t.plan !== 'trial' ? t.plan : 'Trial')} color={dl !== null && dl <= 3 ? '#ef4444' : t.plan !== 'trial' ? '#22c55e' : '#f59e0b'} />
                    </div>
                    <div style={{ color: '#64748b', fontSize: 11, marginTop: 2 }}>{t.email} {'\u00b7'} {days}d since signup</div>
                  </div>
                  <div style={{ textAlign: 'right' }}>
                    <div style={{ fontSize: 20, fontWeight: 700, color: sc }}>{t.setupScore || 0}%</div>
                  </div>
                </div>
                {/* Progress bar */}
                <div style={{ height: 6, background: '#0f172a', borderRadius: 3, overflow: 'hidden', marginBottom: 6 }}>
                  <div style={{ height: '100%', width: (t.setupScore || 0) + '%', background: sc, borderRadius: 3, transition: 'width 0.3s' }} />
                </div>
                {/* Mini checklist */}
                <div style={{ display: 'flex', gap: 4 }}>
                  {checklist.map(function(key) {
                    var done = t.setupSteps && t.setupSteps[key];
                    return (<div key={key} style={{
                      flex: 1, padding: '3px 4px', borderRadius: 4, fontSize: 9, fontWeight: 600, textAlign: 'center',
                      background: done ? '#22c55e12' : '#33415533', color: done ? '#22c55e' : '#475569',
                    }}>
                      {done ? '\u2705' : '\u2b1c'} {checkLabels[key]}
                    </div>);
                  })}
                </div>
                {/* Counts */}
                <div style={{ display: 'flex', gap: 12, marginTop: 6, fontSize: 11, color: '#64748b' }}>
                  <span>{t.staffCount || 0} staff</span>
                  <span>{t.serviceCount || 0} services</span>
                  <span>{t.customerCount || 0} clients</span>
                  <span>{t.bookingCount || 0} bookings</span>
                </div>
              </div>);
            })}
          </div>
        </div>

        {/* RIGHT: DETAIL PANEL */}
        {detail && (
          <div style={{ flex: '0 0 43%', background: '#1e293b', borderRadius: 12, border: '1px solid #334155', padding: 20, maxHeight: '80vh', overflowY: 'auto', position: 'sticky', top: 80 }}>
            {/* Header */}
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start', marginBottom: 16 }}>
              <div>
                <h2 style={{ color: '#f1f5f9', fontSize: 18, fontWeight: 700, margin: 0 }}>{detail.business_name}</h2>
                <div style={{ color: '#64748b', fontSize: 13, marginTop: 2 }}>{detail.email}</div>
                {detail.phone && <div style={{ color: '#64748b', fontSize: 13 }}>{detail.phone}</div>}
              </div>
              <button onClick={function() { setDetail(null); }} style={{ background: 'none', border: 'none', color: '#64748b', cursor: 'pointer' }}><X size={18} /></button>
            </div>

            {/* Score */}
            <div style={{ textAlign: 'center', marginBottom: 16 }}>
              <div style={{ fontSize: 36, fontWeight: 800, color: (detail.setupScore || 0) >= 80 ? '#22c55e' : (detail.setupScore || 0) >= 40 ? '#f59e0b' : '#ef4444' }}>{detail.setupScore || 0}%</div>
              <div style={{ color: '#64748b', fontSize: 12 }}>Setup Complete</div>
              <div style={{ height: 8, background: '#0f172a', borderRadius: 4, marginTop: 8, overflow: 'hidden' }}>
                <div style={{ height: '100%', width: (detail.setupScore || 0) + '%', background: (detail.setupScore || 0) >= 80 ? '#22c55e' : (detail.setupScore || 0) >= 40 ? '#f59e0b' : '#ef4444', borderRadius: 4 }} />
              </div>
            </div>

            {/* Checklist */}
            <div style={{ marginBottom: 16 }}>
              {checklist.map(function(key) {
                var done = detail.setupSteps && detail.setupSteps[key];
                var counts = { hasStaff: detail.staffCount, hasServices: detail.serviceCount, hasCustomers: detail.customerCount, hasBookings: detail.bookingCount };
                return (<div key={key} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '8px 10px', borderRadius: 8, marginBottom: 4, background: done ? '#22c55e08' : '#0f172a' }}>
                  <span style={{ fontSize: 16 }}>{done ? '\u2705' : '\u2b1c'}</span>
                  <span style={{ color: done ? '#22c55e' : '#64748b', fontSize: 13, fontWeight: 600, flex: 1 }}>{checkIcons[key]} {checkLabels[key]}</span>
                  {counts[key] !== undefined && <span style={{ color: '#94a3b8', fontSize: 12 }}>{counts[key]}</span>}
                </div>);
              })}
            </div>

            {/* Info Grid */}
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8, marginBottom: 16, fontSize: 12 }}>
              <div style={{ color: '#64748b' }}>Plan: <span style={{ color: '#e2e8f0', fontWeight: 600 }}>{detail.plan || 'trial'}</span></div>
              <div style={{ color: '#64748b' }}>Status: <span style={{ color: '#e2e8f0' }}>{detail.status || 'trial'}</span></div>
              <div style={{ color: '#64748b' }}>Type: <span style={{ color: '#e2e8f0' }}>{detail.business_type || '\u2014'}</span></div>
              <div style={{ color: '#64748b' }}>Tenant ID: <span style={{ color: '#e2e8f0' }}>{detail.id}</span></div>
            </div>

            {/* ACTIONS */}
            <div style={{ display: 'flex', gap: 8, marginBottom: 16, flexWrap: 'wrap' }}>
              <Btn small onClick={function() { loginAs(detail); }}>Login As</Btn>
              <Btn small outline onClick={function() { extendTrial(detail); }}>+14 Days</Btn>
              <Btn small outline color="#22c55e" onClick={function() { upgradePlan(detail, 'starter'); }}>Upgrade Starter</Btn>
              <Btn small outline color="#a78bfa" onClick={function() { upgradePlan(detail, 'standard'); }}>Upgrade Standard</Btn>
            </div>

            {/* NOTES */}
            <div style={{ background: '#0f172a', borderRadius: 10, padding: 14, border: '1px solid #334155' }}>
              <div style={{ color: '#f1f5f9', fontSize: 13, fontWeight: 600, marginBottom: 8 }}>Onboarding Notes</div>
              <div style={{ display: 'flex', gap: 6, marginBottom: 10 }}>
                <input value={noteText} onChange={function(e) { setNoteText(e.target.value); }} onKeyDown={function(e) { if (e.key === 'Enter') addNote(); }} placeholder="Add a note..." style={{ flex: 1, padding: '8px 12px', background: '#1e293b', border: '1px solid #334155', borderRadius: 6, color: '#e2e8f0', fontSize: 12 }} />
                <Btn small onClick={addNote} disabled={!noteText.trim()}>Add</Btn>
              </div>
              {notesLoading ? <div style={{ color: '#64748b', fontSize: 12 }}>Loading...</div> :
               notes.length === 0 ? <div style={{ color: '#475569', fontSize: 12, fontStyle: 'italic' }}>No notes yet.</div> :
               notes.map(function(n) {
                return (<div key={n.id} style={{ padding: '6px 0', borderBottom: '1px solid #33415544' }}>
                  <div style={{ color: '#e2e8f0', fontSize: 12 }}>{n.content}</div>
                  <div style={{ color: '#475569', fontSize: 10, marginTop: 2 }}>{timeAgo(n.created_at)}</div>
                </div>);
              })}
            </div>
          </div>
        )}
      </div>
    </PageShell>
  );
}

// ════════════════════════════════════════════════════════════════
// OUTREACH
// ════════════════════════════════════════════════════════════════
export function AdminCampaigns() {
  var [leads, setLeads] = useState([]);
  var [templates, setTemplates] = useState([]);
  var [campaigns, setCampaigns] = useState([]);
  var [stats, setStats] = useState({});
  var [loading, setLoading] = useState(true);
  var [selectedLeads, setSelectedLeads] = useState([]);
  var [selTemplate, setSelTemplate] = useState(null);
  var [customMsg, setCustomMsg] = useState('');
  var [sendType, setSendType] = useState('sms');
  var [sending, setSending] = useState(false);
  var [sendResult, setSendResult] = useState(null);
  var [filterType, setFilterType] = useState('all');
  var [filterStage, setFilterStage] = useState('all');
  var [campaignName, setCampaignName] = useState('');
  var [tab, setTab] = useState('compose');
  var [detailLead, setDetailLead] = useState(null);
  var [leadMessages, setLeadMessages] = useState([]);
  var [leadMsgLoading, setLeadMsgLoading] = useState(false);
  var [quickMsg, setQuickMsg] = useState('');
  var [quickSending, setQuickSending] = useState(false);
  var [campDetail, setCampDetail] = useState(null);
  var [campMessages, setCampMessages] = useState([]);
  var [campLoading, setCampLoading] = useState(false);
  var [previewLead, setPreviewLead] = useState(null);

  useEffect(function() {
    Promise.all([
      axios.get('/api/admin/leads'),
      axios.get('/api/admin/leads/outreach/templates'),
      axios.get('/api/admin/leads/outreach/campaigns').catch(function() { return { data: { campaigns: [] } }; }),
      axios.get('/api/admin/leads/outreach/stats').catch(function() { return { data: { stats: {} } }; }),
    ]).then(function(results) {
      var raw = results[0].data.leads || [];
      setLeads(raw.map(function(l) { return Object.assign({}, l, { business: l.business_name || '', contact: l.contact_name || '', type: l.business_type || '' }); }));
      setTemplates(results[1].data.templates || []);
      setCampaigns(results[2].data.campaigns || []);
      setStats(results[3].data.stats || {});
      setLoading(false);
    });
  }, []);

  var filteredLeads = leads.filter(function(l) {
    if (filterStage !== 'all' && l.stage !== filterStage) return false;
    if (filterType !== 'all' && l.business_type !== filterType) return false;
    if (sendType === 'sms' && !l.phone) return false;
    if (sendType === 'email' && !l.email) return false;
    return l.stage !== 'won' && l.stage !== 'lost';
  });

  var toggleLead = function(id) { setSelectedLeads(function(p) { return p.includes(id) ? p.filter(function(x) { return x !== id; }) : p.concat([id]); }); };
  var selectAll = function() { setSelectedLeads(filteredLeads.map(function(l) { return l.id; })); };
  var clearAll = function() { setSelectedLeads([]); };

  var pickTemplate = function(t) { setSelTemplate(t); setCustomMsg(t.body); setSendType(t.type || 'sms'); };

  var personalize = function(msg, lead) {
    return msg.replace(/\{contact\}/g, lead.contact || lead.contact_name || 'there').replace(/\{business\}/g, lead.business || lead.business_name || 'your business').replace(/\{type\}/g, lead.type || lead.business_type || 'service');
  };

  var openLeadDetail = async function(lead) {
    setDetailLead(lead);
    setLeadMsgLoading(true);
    setQuickMsg(customMsg || '');
    try {
      var res = await axios.get('/api/admin/leads/outreach/lead/' + lead.id);
      setLeadMessages(res.data.messages || []);
    } catch(e) { setLeadMessages([]); }
    setLeadMsgLoading(false);
  };

  var quickSend = async function() {
    if (!detailLead || !quickMsg) return;
    setQuickSending(true);
    try {
      await axios.post('/api/admin/leads/outreach/quick-send', {
        lead_id: detailLead.id,
        type: sendType,
        content: quickMsg,
      });
      var res = await axios.get('/api/admin/leads/outreach/lead/' + detailLead.id);
      setLeadMessages(res.data.messages || []);
      var sRes = await axios.get('/api/admin/leads/outreach/stats').catch(function() { return { data: { stats: {} } }; });
      setStats(sRes.data.stats || {});
    } catch(e) { alert('Error: ' + (e.response?.data?.error || e.message)); }
    setQuickSending(false);
  };

  var openCampaignDetail = async function(camp) {
    setCampDetail(camp);
    setCampLoading(true);
    try {
      var res = await axios.get('/api/admin/leads/outreach/campaigns/' + camp.id);
      setCampMessages(res.data.messages || []);
    } catch(e) { setCampMessages([]); }
    setCampLoading(false);
  };

  var sendOutreach = async function() {
    if (selectedLeads.length === 0 || !customMsg) return;
    setSending(true); setSendResult(null);
    try {
      var res = await axios.post('/api/admin/leads/outreach/bulk-send', {
        lead_ids: selectedLeads, type: sendType, template: customMsg,
        campaign_name: campaignName || (selTemplate ? selTemplate.name : 'Manual Outreach'),
      });
      setSendResult(res.data);
      setSelectedLeads([]);
      var cRes = await axios.get('/api/admin/leads/outreach/campaigns').catch(function() { return { data: { campaigns: [] } }; });
      setCampaigns(cRes.data.campaigns || []);
      var sRes = await axios.get('/api/admin/leads/outreach/stats').catch(function() { return { data: { stats: {} } }; });
      setStats(sRes.data.stats || {});
      var lRes = await axios.get('/api/admin/leads');
      setLeads((lRes.data.leads || []).map(function(l) { return Object.assign({}, l, { business: l.business_name || '', contact: l.contact_name || '', type: l.business_type || '' }); }));
    } catch(e) { setSendResult({ error: e.response?.data?.error || e.message }); }
    setSending(false);
  };

  var timeAgo = function(d) { if (!d) return ''; var m = Math.floor((Date.now() - new Date(d).getTime()) / 60000); if (m < 60) return m + 'm ago'; if (m < 1440) return Math.floor(m / 60) + 'h ago'; return Math.floor(m / 1440) + 'd ago'; };

  return (
    <PageShell title="Outreach" icon={Megaphone}>
      {/* STATS */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(130px, 1fr))', gap: 12, marginBottom: 20 }}>
        <StatCard label="Total Sent" value={stats.totalSent || 0} icon={Megaphone} color="#a78bfa" />
        <StatCard label="This Week" value={stats.sentThisWeek || 0} icon={ArrowUpRight} color="#22c55e" />
        <StatCard label="SMS Sent" value={stats.smsSent || 0} icon={Phone} color="#3b82f6" />
        <StatCard label="Emails Sent" value={stats.emailSent || 0} icon={Mail} color="#f59e0b" />
        <StatCard label="Campaigns" value={stats.campaigns || 0} icon={Target} color="#06b6d4" />
      </div>

      {/* TABS */}
      <div style={{ display: 'flex', gap: 8, marginBottom: 20 }}>
        {[{k:'compose',l:'Compose'},{k:'templates',l:'Templates'},{k:'history',l:'Campaign History'}].map(function(t) {
          return <Btn key={t.k} small outline={tab !== t.k} onClick={function() { setTab(t.k); setDetailLead(null); setCampDetail(null); }}>{t.l}</Btn>;
        })}
      </div>

      {/* ═══ COMPOSE TAB ═══ */}
      {tab === 'compose' && (
        <div style={{ display: 'flex', gap: 16 }}>
          {/* LEFT: Select Leads */}
          <div style={{ flex: detailLead ? '0 0 35%' : '0 0 50%', minWidth: 0 }}>
            <div style={{ background: '#1e293b', borderRadius: 12, border: '1px solid #334155', padding: 16 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
                <h3 style={{ color: '#f1f5f9', fontSize: 14, fontWeight: 600, margin: 0 }}>Recipients ({selectedLeads.length} selected)</h3>
                <div style={{ display: 'flex', gap: 4 }}>
                  <Btn small outline onClick={selectAll}>All</Btn>
                  <Btn small outline onClick={clearAll}>None</Btn>
                </div>
              </div>
              <div style={{ display: 'flex', gap: 6, marginBottom: 10, flexWrap: 'wrap' }}>
                <select value={sendType} onChange={function(e) { setSendType(e.target.value); setSelectedLeads([]); }} style={{ background: '#0f172a', border: '1px solid #334155', borderRadius: 6, color: '#e2e8f0', fontSize: 12, padding: '4px 8px' }}>
                  <option value="sms">SMS</option><option value="email">Email</option>
                </select>
                <select value={filterStage} onChange={function(e) { setFilterStage(e.target.value); }} style={{ background: '#0f172a', border: '1px solid #334155', borderRadius: 6, color: '#e2e8f0', fontSize: 12, padding: '4px 8px' }}>
                  <option value="all">All Stages</option><option value="new">New</option><option value="contacted">Contacted</option><option value="demo_scheduled">Demo Set</option>
                </select>
                <select value={filterType} onChange={function(e) { setFilterType(e.target.value); }} style={{ background: '#0f172a', border: '1px solid #334155', borderRadius: 6, color: '#e2e8f0', fontSize: 12, padding: '4px 8px' }}>
                  <option value="all">All Types</option><option value="spa">Spa</option><option value="barbershop">Barber</option><option value="auto">Auto</option><option value="smokeshop">Smoke</option>
                </select>
              </div>
              <div style={{ maxHeight: 450, overflowY: 'auto' }}>
                {filteredLeads.length === 0 ? <div style={{ color: '#64748b', fontSize: 12, padding: 20, textAlign: 'center' }}>No leads match{sendType === 'sms' ? ' (need phone)' : ' (need email)'}</div> :
                 filteredLeads.map(function(l) {
                  var checked = selectedLeads.includes(l.id);
                  var isActive = detailLead && detailLead.id === l.id;
                  var stage = LEAD_STAGES.find(function(s) { return s.key === l.stage; }) || LEAD_STAGES[0];
                  return (<div key={l.id} style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '7px 10px', cursor: 'pointer',
                    background: isActive ? '#a78bfa15' : checked ? '#a78bfa08' : 'transparent',
                    borderBottom: '1px solid #33415533', borderLeft: isActive ? '3px solid #a78bfa' : '3px solid transparent',
                  }}>
                    <input type="checkbox" checked={checked} onChange={function(e) { e.stopPropagation(); toggleLead(l.id); }} style={{ cursor: 'pointer' }} />
                    <div onClick={function() { openLeadDetail(l); }} style={{ flex: 1, minWidth: 0, cursor: 'pointer' }}>
                      <div style={{ display: 'flex', gap: 4, alignItems: 'center' }}>
                        <span style={{ color: '#f1f5f9', fontSize: 12, fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{l.business || l.contact}</span>
                        <Badge text={stage.label} color={stage.color} />
                      </div>
                      <div style={{ color: '#64748b', fontSize: 10 }}>{sendType === 'sms' ? l.phone : l.email}</div>
                    </div>
                  </div>);
                })}
              </div>
            </div>
          </div>

          {/* MIDDLE: Compose (or shrink if detail open) */}
          <div style={{ flex: detailLead ? '0 0 30%' : '0 0 48%', minWidth: 0 }}>
            <div style={{ background: '#1e293b', borderRadius: 12, border: '1px solid #334155', padding: 16 }}>
              <h3 style={{ color: '#f1f5f9', fontSize: 14, fontWeight: 600, marginBottom: 10 }}>Compose</h3>
              {/* Template buttons */}
              <div style={{ display: 'flex', gap: 4, marginBottom: 10, flexWrap: 'wrap' }}>
                {templates.filter(function(t) { return t.type === sendType || sendType === 'sms'; }).map(function(t) {
                  return <button key={t.id} onClick={function() { pickTemplate(t); }} style={{
                    padding: '4px 8px', background: selTemplate && selTemplate.id === t.id ? '#a78bfa33' : '#0f172a',
                    border: '1px solid ' + (selTemplate && selTemplate.id === t.id ? '#a78bfa' : '#334155'),
                    borderRadius: 5, color: selTemplate && selTemplate.id === t.id ? '#a78bfa' : '#94a3b8', fontSize: 10, fontWeight: 600, cursor: 'pointer',
                  }}>{t.name}</button>;
                })}
              </div>
              <input value={campaignName} onChange={function(e) { setCampaignName(e.target.value); }} placeholder="Campaign name (optional)" style={{ width: '100%', padding: '7px 10px', background: '#0f172a', border: '1px solid #334155', borderRadius: 6, color: '#e2e8f0', fontSize: 12, marginBottom: 6 }} />
              <textarea value={customMsg} onChange={function(e) { setCustomMsg(e.target.value); }} placeholder="Write your message... Use {contact}, {business}, {type}" rows={6} style={{ width: '100%', padding: '8px 10px', background: '#0f172a', border: '1px solid #334155', borderRadius: 6, color: '#e2e8f0', fontSize: 12, resize: 'vertical', marginBottom: 6 }} />
              <div style={{ color: '#475569', fontSize: 10, marginBottom: 8 }}>Variables: <span style={{ color: '#a78bfa' }}>{'{contact}'}</span> <span style={{ color: '#a78bfa' }}>{'{business}'}</span> <span style={{ color: '#a78bfa' }}>{'{type}'}</span></div>

              {/* Preview */}
              {customMsg && selectedLeads.length > 0 && (
                <div style={{ background: '#0f172a', borderRadius: 8, padding: 10, marginBottom: 8, border: '1px solid #334155' }}>
                  <div style={{ color: '#64748b', fontSize: 10, fontWeight: 600, marginBottom: 4 }}>PREVIEW (first recipient):</div>
                  <div style={{ color: '#e2e8f0', fontSize: 11, whiteSpace: 'pre-wrap' }}>
                    {personalize(customMsg, leads.find(function(l) { return l.id === selectedLeads[0]; }) || {})}
                  </div>
                </div>
              )}

              {sendResult && (<div style={{ marginBottom: 8, padding: 8, borderRadius: 6, background: sendResult.error ? '#ef444422' : '#22c55e22', border: '1px solid ' + (sendResult.error ? '#ef4444' : '#22c55e') }}>
                {sendResult.error ? <div style={{ color: '#ef4444', fontSize: 11 }}>{sendResult.error}</div> :
                 <div style={{ color: '#22c55e', fontSize: 11 }}>Sent to <strong>{sendResult.sent}</strong> leads{sendResult.failed > 0 && <span style={{ color: '#f59e0b' }}> ({sendResult.failed} failed)</span>}</div>}
              </div>)}

              <Btn onClick={sendOutreach} disabled={sending || selectedLeads.length === 0 || !customMsg}>
                {sending ? 'Sending...' : (sendType === 'sms' ? <><Phone size={14} /> Send SMS to {selectedLeads.length}</> : <><Mail size={14} /> Email {selectedLeads.length}</>)}
              </Btn>
            </div>
          </div>

          {/* RIGHT: Lead Detail (only when lead clicked) */}
          {detailLead && (
            <div style={{ flex: '0 0 33%', background: '#1e293b', borderRadius: 12, border: '1px solid #334155', padding: 16, maxHeight: '75vh', overflowY: 'auto', position: 'sticky', top: 80 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start', marginBottom: 12 }}>
                <div>
                  <h3 style={{ color: '#f1f5f9', fontSize: 16, fontWeight: 700, margin: 0 }}>{detailLead.business || detailLead.contact}</h3>
                  <div style={{ color: '#64748b', fontSize: 12, marginTop: 2 }}>{detailLead.contact}</div>
                </div>
                <button onClick={function() { setDetailLead(null); }} style={{ background: 'none', border: 'none', color: '#64748b', cursor: 'pointer' }}><X size={16} /></button>
              </div>

              {/* Contact */}
              <div style={{ display: 'flex', gap: 6, marginBottom: 12, flexWrap: 'wrap' }}>
                {detailLead.phone && <a href={'tel:' + detailLead.phone} style={{ display: 'inline-flex', alignItems: 'center', gap: 4, padding: '6px 10px', background: '#22c55e12', border: '1px solid #22c55e33', borderRadius: 6, color: '#22c55e', fontSize: 11, fontWeight: 600, textDecoration: 'none' }}><Phone size={12} /> {detailLead.phone}</a>}
                {detailLead.email && <a href={'mailto:' + detailLead.email} style={{ display: 'inline-flex', alignItems: 'center', gap: 4, padding: '6px 10px', background: '#3b82f612', border: '1px solid #3b82f633', borderRadius: 6, color: '#3b82f6', fontSize: 11, fontWeight: 600, textDecoration: 'none' }}><Mail size={12} /> {detailLead.email}</a>}
              </div>

              {/* Quick Send */}
              <div style={{ background: '#0f172a', borderRadius: 8, padding: 12, marginBottom: 14, border: '1px solid #334155' }}>
                <div style={{ color: '#f1f5f9', fontSize: 12, fontWeight: 600, marginBottom: 6 }}>Quick Send to {detailLead.contact || detailLead.business}</div>
                <textarea value={quickMsg} onChange={function(e) { setQuickMsg(e.target.value); }} rows={3} style={{ width: '100%', padding: '6px 8px', background: '#1e293b', border: '1px solid #334155', borderRadius: 6, color: '#e2e8f0', fontSize: 11, resize: 'vertical', marginBottom: 6 }} />
                {/* Personalized preview */}
                {quickMsg && (
                  <div style={{ background: '#1e293b', borderRadius: 6, padding: 8, marginBottom: 6, fontSize: 10, color: '#94a3b8' }}>
                    <span style={{ color: '#64748b', fontWeight: 600 }}>Preview: </span>
                    {personalize(quickMsg, detailLead)}
                  </div>
                )}
                <Btn small onClick={quickSend} disabled={quickSending || !quickMsg}>
                  {quickSending ? 'Sending...' : (sendType === 'sms' ? <><Phone size={12} /> Send SMS</> : <><Mail size={12} /> Send Email</>)}
                </Btn>
              </div>

              {/* Message History */}
              <div style={{ color: '#f1f5f9', fontSize: 12, fontWeight: 600, marginBottom: 8 }}>
                Message History <span style={{ color: '#64748b', fontWeight: 400 }}>({leadMessages.length})</span>
              </div>
              {leadMsgLoading ? <div style={{ color: '#64748b', fontSize: 11 }}>Loading...</div> :
               leadMessages.length === 0 ? <div style={{ color: '#475569', fontSize: 11, fontStyle: 'italic' }}>No messages sent yet.</div> :
               leadMessages.map(function(m, idx) {
                return (<div key={m.id} style={{ padding: '8px 0', borderBottom: idx < leadMessages.length - 1 ? '1px solid #33415533' : 'none' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 3 }}>
                    <Badge text={m.type.toUpperCase()} color={m.type === 'sms' ? '#a78bfa' : '#3b82f6'} />
                    <span style={{ color: '#475569', fontSize: 9 }}>{timeAgo(m.created_at)}</span>
                  </div>
                  <div style={{ color: '#94a3b8', fontSize: 11, whiteSpace: 'pre-wrap', lineHeight: 1.4 }}>{m.content}</div>
                  <div style={{ color: '#475569', fontSize: 9, marginTop: 2 }}>To: {m.to_address}</div>
                </div>);
              })}
            </div>
          )}
        </div>
      )}

      {/* ═══ TEMPLATES TAB ═══ */}
      {tab === 'templates' && (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: 16 }}>
          {templates.map(function(t) {
            return (<div key={t.id} style={{ background: '#1e293b', borderRadius: 12, border: '1px solid #334155', padding: 16 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
                <h4 style={{ color: '#f1f5f9', fontSize: 14, fontWeight: 600, margin: 0 }}>{t.name}</h4>
                <Badge text={t.type.toUpperCase()} color={t.type === 'sms' ? '#a78bfa' : '#3b82f6'} />
              </div>
              <div style={{ color: '#94a3b8', fontSize: 12, lineHeight: 1.5, marginBottom: 12, whiteSpace: 'pre-wrap' }}>{t.body}</div>
              <div style={{ display: 'flex', gap: 6 }}>
                <Btn small onClick={function() { pickTemplate(t); setTab('compose'); }}>Use Template</Btn>
                <Btn small outline onClick={function() { navigator.clipboard.writeText(t.body); }}>Copy</Btn>
              </div>
            </div>);
          })}
        </div>
      )}

      {/* ═══ HISTORY TAB ═══ */}
      {tab === 'history' && (
        <div style={{ display: 'flex', gap: 16 }}>
          {/* LEFT: Campaign List */}
          <div style={{ flex: campDetail ? '0 0 45%' : '1 1 100%', minWidth: 0 }}>
            <div style={{ background: '#1e293b', borderRadius: 12, border: '1px solid #334155' }}>
              {campaigns.length === 0 ? <div style={{ padding: 40, textAlign: 'center', color: '#64748b' }}>No campaigns sent yet.</div> :
               campaigns.map(function(c, idx) {
                var isActive = campDetail && campDetail.id === c.id;
                return (<div key={c.id} onClick={function() { openCampaignDetail(c); }} style={{
                  padding: '14px 18px', borderBottom: idx < campaigns.length - 1 ? '1px solid #334155' : 'none',
                  display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                  cursor: 'pointer', background: isActive ? '#a78bfa12' : 'transparent',
                  borderLeft: isActive ? '3px solid #a78bfa' : '3px solid transparent',
                }}>
                  <div>
                    <div style={{ color: '#f1f5f9', fontWeight: 600, fontSize: 14 }}>{c.name}</div>
                    <div style={{ color: '#64748b', fontSize: 12 }}>{c.type.toUpperCase()} {'\u00b7'} {timeAgo(c.created_at)} {'\u00b7'} {new Date(c.created_at).toLocaleDateString()}</div>
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                    <div style={{ textAlign: 'center' }}>
                      <div style={{ color: '#a78bfa', fontWeight: 700, fontSize: 20 }}>{c.total_sent}</div>
                      <div style={{ color: '#64748b', fontSize: 10 }}>Sent</div>
                    </div>
                    <Badge text={c.status || 'sent'} color={c.status === 'sent' ? '#22c55e' : '#64748b'} />
                  </div>
                </div>);
              })}
            </div>
          </div>

          {/* RIGHT: Campaign Detail */}
          {campDetail && (
            <div style={{ flex: '0 0 53%', background: '#1e293b', borderRadius: 12, border: '1px solid #334155', padding: 20, maxHeight: '75vh', overflowY: 'auto', position: 'sticky', top: 80 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start', marginBottom: 16 }}>
                <div>
                  <h3 style={{ color: '#f1f5f9', fontSize: 18, fontWeight: 700, margin: 0 }}>{campDetail.name}</h3>
                  <div style={{ color: '#64748b', fontSize: 12, marginTop: 2 }}>{campDetail.type.toUpperCase()} {'\u00b7'} {new Date(campDetail.created_at).toLocaleString()}</div>
                </div>
                <button onClick={function() { setCampDetail(null); }} style={{ background: 'none', border: 'none', color: '#64748b', cursor: 'pointer' }}><X size={18} /></button>
              </div>

              {/* Campaign Stats */}
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 12, marginBottom: 16 }}>
                <div style={{ background: '#0f172a', borderRadius: 8, padding: 12, textAlign: 'center' }}>
                  <div style={{ color: '#a78bfa', fontSize: 24, fontWeight: 700 }}>{campDetail.total_sent}</div>
                  <div style={{ color: '#64748b', fontSize: 10 }}>Total Sent</div>
                </div>
                <div style={{ background: '#0f172a', borderRadius: 8, padding: 12, textAlign: 'center' }}>
                  <div style={{ color: '#22c55e', fontSize: 24, fontWeight: 700 }}>{campMessages.length}</div>
                  <div style={{ color: '#64748b', fontSize: 10 }}>Messages</div>
                </div>
                <div style={{ background: '#0f172a', borderRadius: 8, padding: 12, textAlign: 'center' }}>
                  <div style={{ color: '#3b82f6', fontSize: 24, fontWeight: 700 }}>{[...new Set(campMessages.map(function(m) { return m.lead_id; }))].length}</div>
                  <div style={{ color: '#64748b', fontSize: 10 }}>Leads</div>
                </div>
              </div>

              {/* Message template used */}
              {campDetail.template && (
                <div style={{ background: '#0f172a', borderRadius: 8, padding: 12, marginBottom: 16, border: '1px solid #334155' }}>
                  <div style={{ color: '#64748b', fontSize: 10, fontWeight: 600, marginBottom: 4 }}>TEMPLATE USED:</div>
                  <div style={{ color: '#94a3b8', fontSize: 11, whiteSpace: 'pre-wrap' }}>{campDetail.template}</div>
                </div>
              )}

              {/* Messages sent */}
              <div style={{ color: '#f1f5f9', fontSize: 13, fontWeight: 600, marginBottom: 8 }}>Messages ({campMessages.length})</div>
              {campLoading ? <div style={{ color: '#64748b', fontSize: 12 }}>Loading...</div> :
               campMessages.length === 0 ? <div style={{ color: '#475569', fontSize: 12 }}>No messages found.</div> :
               campMessages.map(function(m, idx) {
                return (<div key={m.id} style={{ padding: '10px 0', borderBottom: idx < campMessages.length - 1 ? '1px solid #33415533' : 'none' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 4 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                      <span style={{ color: '#f1f5f9', fontWeight: 600, fontSize: 12 }}>{m.business_name}</span>
                      <span style={{ color: '#64748b', fontSize: 11 }}>{m.contact_name}</span>
                    </div>
                    <span style={{ color: '#475569', fontSize: 10 }}>{timeAgo(m.created_at)}</span>
                  </div>
                  <div style={{ color: '#94a3b8', fontSize: 11, whiteSpace: 'pre-wrap', lineHeight: 1.4, maxHeight: 60, overflow: 'hidden' }}>{m.content}</div>
                  <div style={{ color: '#475569', fontSize: 9, marginTop: 2 }}>To: {m.to_address}</div>
                </div>);
              })}
            </div>
          )}
        </div>
      )}
    </PageShell>
  );
}

// ════════════════════════════════════════════════════════════════
// ACTIVITY FEED — Real data from admin API
// ════════════════════════════════════════════════════════════════

// ════════════════════════════════════════════════════════════════
// ACTIVITY FEED V3 — Multi-source feed with filters & stats
// ════════════════════════════════════════════════════════════════
export function AdminActivity() {
  var [activities, setActivities] = useState([]);
  var [auditLogs, setAuditLogs] = useState([]);
  var [loading, setLoading] = useState(true);
  var [filter, setFilter] = useState('all');
  var [tenantFilter, setTenantFilter] = useState('all');
  var [autoRefresh, setAutoRefresh] = useState(false);
  var [searchTerm, setSearchTerm] = useState('');

  var load = function() {
    setLoading(true);
    Promise.all([
      axios.get('/api/admin/activity-feed?limit=50').catch(function() { return { data: { activities: [] } }; }),
      axios.get('/api/admin/audit-log?limit=30').catch(function() { return { data: { logs: [] } }; }),
    ]).then(function(results) {
      setActivities(results[0].data.activities || []);
      setAuditLogs(results[1].data.logs || []);
      setLoading(false);
    });
  };

  useEffect(function() { load(); }, []);
  useEffect(function() {
    if (!autoRefresh) return;
    var iv = setInterval(load, 15000);
    return function() { clearInterval(iv); };
  }, [autoRefresh]);

  var timeAgo = function(d) { if (!d) return ''; var m = Math.floor((Date.now() - new Date(d).getTime()) / 60000); if (m < 1) return 'just now'; if (m < 60) return m + 'm ago'; if (m < 1440) return Math.floor(m / 60) + 'h ago'; return Math.floor(m / 1440) + 'd ago'; };

  // Merge activities + audit into unified feed
  var allItems = [];
  activities.forEach(function(a) {
    allItems.push({ type: a.type, icon: a.icon, text: a.text, business: a.business, time: a.time, status: a.status, source: 'activity' });
  });
  auditLogs.forEach(function(l) {
    allItems.push({ type: 'admin', icon: '🔧', text: l.action + (l.target ? ' → ' + l.target : ''), business: l.user_email, time: l.created_at, source: 'audit' });
  });
  allItems.sort(function(a, b) { return new Date(b.time) - new Date(a.time); });

  // Filters
  var filtered = allItems.filter(function(a) {
    if (filter !== 'all' && a.type !== filter) return false;
    if (tenantFilter !== 'all' && a.business !== tenantFilter) return false;
    if (searchTerm && !(a.text + ' ' + a.business).toLowerCase().includes(searchTerm.toLowerCase())) return false;
    return true;
  });

  // Stats
  var now = new Date();
  var todayStr = now.toISOString().split('T')[0];
  var todayItems = activities.filter(function(a) { return (a.time || '').startsWith(todayStr); });
  var todayBookings = todayItems.filter(function(a) { return a.type === 'booking'; }).length;
  var todayClients = todayItems.filter(function(a) { return a.type === 'new_client'; }).length;
  var todaySMS = todayItems.filter(function(a) { return a.type === 'sms'; }).length;
  var todaySignups = todayItems.filter(function(a) { return a.type === 'signup'; }).length;

  // Unique tenants for filter
  var tenants = [];
  var seen = {};
  activities.forEach(function(a) { if (a.business && !seen[a.business]) { seen[a.business] = true; tenants.push(a.business); } });
  tenants.sort();

  var types = [
    { key: 'all', label: 'All', icon: '📋' },
    { key: 'booking', label: 'Bookings', icon: '📅' },
    { key: 'new_client', label: 'Clients', icon: '👤' },
    { key: 'signup', label: 'Signups', icon: '🚀' },
    { key: 'sms', label: 'SMS', icon: '📱' },
    { key: 'admin', label: 'Admin', icon: '🔧' },
  ];

  var typeCounts = {};
  allItems.forEach(function(a) { typeCounts[a.type] = (typeCounts[a.type] || 0) + 1; });
  typeCounts.all = allItems.length;

  return (
    React.createElement(PageShell, { title: 'Activity Feed', icon: Activity, action:
      React.createElement('div', { style: { display: 'flex', gap: 8, alignItems: 'center' } },
        React.createElement('label', { style: { display: 'flex', alignItems: 'center', gap: 6, color: autoRefresh ? '#22c55e' : '#64748b', fontSize: 12, cursor: 'pointer' } },
          React.createElement('input', { type: 'checkbox', checked: autoRefresh, onChange: function() { setAutoRefresh(!autoRefresh); }, style: { accentColor: '#a78bfa' } }),
          'Auto 15s'
        ),
        React.createElement(Btn, { small: true, outline: true, onClick: load }, React.createElement(RefreshCw, { size: 14 }), ' Refresh')
      )
    },
      // Stats bar
      React.createElement('div', { style: { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', gap: 12, marginBottom: 20 } },
        React.createElement(StatCard, { label: 'Today Total', value: todayItems.length, icon: Activity, color: '#a78bfa' }),
        React.createElement(StatCard, { label: 'Bookings', value: todayBookings, icon: Calendar, color: '#3b82f6' }),
        React.createElement(StatCard, { label: 'New Clients', value: todayClients, icon: UserPlus, color: '#22c55e' }),
        React.createElement(StatCard, { label: 'SMS Sent', value: todaySMS, icon: MessageSquare, color: '#f59e0b' }),
        React.createElement(StatCard, { label: 'Signups', value: todaySignups, icon: TrendingUp, color: '#ec4899' })
      ),

      // Filter bar
      React.createElement('div', { style: { display: 'flex', gap: 8, marginBottom: 16, flexWrap: 'wrap', alignItems: 'center' } },
        types.map(function(t) {
          var active = filter === t.key;
          return React.createElement('button', {
            key: t.key,
            onClick: function() { setFilter(t.key); },
            style: { padding: '6px 14px', borderRadius: 8, border: '1px solid ' + (active ? '#a78bfa' : '#334155'), background: active ? '#a78bfa22' : 'transparent', color: active ? '#a78bfa' : '#94a3b8', fontSize: 13, cursor: 'pointer', fontWeight: active ? 600 : 400 }
          }, t.icon + ' ' + t.label + ' (' + (typeCounts[t.key] || 0) + ')');
        }),
        // Tenant dropdown
        React.createElement('select', {
          value: tenantFilter,
          onChange: function(e) { setTenantFilter(e.target.value); },
          style: { padding: '6px 12px', borderRadius: 8, border: '1px solid #334155', background: '#0f172a', color: '#94a3b8', fontSize: 13, marginLeft: 'auto' }
        },
          React.createElement('option', { value: 'all' }, 'All Tenants'),
          tenants.map(function(t) { return React.createElement('option', { key: t, value: t }, t); })
        ),
        // Search
        React.createElement('div', { style: { position: 'relative' } },
          React.createElement(Search, { size: 14, style: { position: 'absolute', left: 10, top: 9, color: '#475569' } }),
          React.createElement('input', {
            value: searchTerm,
            onChange: function(e) { setSearchTerm(e.target.value); },
            placeholder: 'Search...',
            style: { padding: '6px 12px 6px 30px', borderRadius: 8, border: '1px solid #334155', background: '#0f172a', color: '#f1f5f9', fontSize: 13, width: 160 }
          })
        )
      ),

      // Feed list
      React.createElement('div', { style: { background: '#1e293b', borderRadius: 12, border: '1px solid #334155', maxHeight: 600, overflowY: 'auto' } },
        loading ? React.createElement('div', { style: { padding: 40, textAlign: 'center', color: '#64748b' } }, 'Loading...') :
        filtered.length === 0 ? React.createElement('div', { style: { padding: 40, textAlign: 'center', color: '#64748b' } }, 'No matching activity') :
        filtered.map(function(a, idx) {
          var sc = { confirmed: '#22c55e', pending: '#f59e0b', completed: '#3b82f6', cancelled: '#ef4444' }[a.status] || '#64748b';
          var typeBg = { booking: '#3b82f622', new_client: '#22c55e22', signup: '#ec489922', sms: '#f59e0b22', admin: '#a78bfa22' }[a.type] || '#33415522';
          return React.createElement('div', {
            key: idx,
            style: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '10px 16px', borderBottom: idx < filtered.length - 1 ? '1px solid #1e293b' : 'none', borderLeft: '3px solid ' + (sc !== '#64748b' ? sc : 'transparent'), background: idx % 2 === 0 ? 'transparent' : '#0f172a11' }
          },
            React.createElement('div', { style: { display: 'flex', alignItems: 'center', gap: 10, flex: 1 } },
              React.createElement('span', { style: { fontSize: 18, width: 28, textAlign: 'center' } }, a.icon),
              React.createElement('div', { style: { flex: 1 } },
                React.createElement('div', { style: { color: '#f1f5f9', fontSize: 13 } }, a.text),
                React.createElement('div', { style: { color: '#64748b', fontSize: 11, marginTop: 2 } }, a.business)
              )
            ),
            React.createElement('div', { style: { display: 'flex', alignItems: 'center', gap: 8, flexShrink: 0 } },
              a.status ? React.createElement(Badge, { text: a.status, color: sc }) : null,
              React.createElement('span', { style: { background: typeBg, color: '#94a3b8', padding: '2px 8px', borderRadius: 6, fontSize: 10, fontWeight: 600, textTransform: 'uppercase' } }, a.type === 'new_client' ? 'client' : a.type),
              React.createElement('span', { style: { color: '#475569', fontSize: 11, minWidth: 55, textAlign: 'right' } }, timeAgo(a.time))
            )
          );
        })
      ),

      // Footer count
      React.createElement('div', { style: { textAlign: 'center', color: '#475569', fontSize: 12, marginTop: 12 } },
        'Showing ' + filtered.length + ' of ' + allItems.length + ' activities',
        autoRefresh ? ' • Auto-refreshing every 15s' : ''
      )
    )
  );
}

// ════════════════════════════════════════════════════════════════
// ANALYTICS V3 — Real charts, breakdowns, pipeline funnel
// ════════════════════════════════════════════════════════════════
export function AdminAnalytics() {
  var [dashboard, setDashboard] = useState(null);
  var [mrr, setMrr] = useState([]);
  var [breakdown, setBreakdown] = useState(null);
  var [pipeline, setPipeline] = useState(null);
  var [loading, setLoading] = useState(true);
  var [chartView, setChartView] = useState('revenue');
  var [period, setPeriod] = useState('30d');

  useEffect(function() {
    Promise.all([
      axios.get('/api/admin/dashboard').catch(function() { return { data: { dashboard: { stats: {} } } }; }),
      axios.get('/api/admin/mrr-history').catch(function() { return { data: { history: [] } }; }),
      axios.get('/api/admin/analytics/breakdown').catch(function() { return { data: {} }; }),
      axios.get('/api/admin/pipeline').catch(function() { return { data: { pipeline: {} } }; }),
    ]).then(function(r) {
      setDashboard(r[0].data.dashboard);
      setMrr(r[1].data.history || []);
      setBreakdown(r[2].data);
      setPipeline(r[3].data.pipeline);
      setLoading(false);
    });
  }, []);

  var s = dashboard?.stats || {};

  // Chart helper — CSS bar chart
  var BarChart = function(props) {
    var data = props.data || [];
    var valueKey = props.valueKey || 'value';
    var color = props.color || '#a78bfa';
    var height = props.height || 180;
    var maxVal = Math.max.apply(null, data.map(function(d) { return d[valueKey] || 0; }).concat([1]));
    var showEvery = data.length > 15 ? 5 : data.length > 7 ? 3 : 1;

    return React.createElement('div', { style: { height: height, display: 'flex', alignItems: 'flex-end', gap: 2, padding: '0 4px' } },
      data.map(function(d, i) {
        var val = d[valueKey] || 0;
        var pct = Math.max((val / maxVal) * 100, val > 0 ? 3 : 0);
        var label = d.label || '';
        return React.createElement('div', {
          key: i,
          style: { flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', height: '100%', justifyContent: 'flex-end' },
          title: label + ': ' + (typeof val === 'number' ? (val % 1 === 0 ? val : val.toFixed(0)) : val)
        },
          val > 0 ? React.createElement('span', { style: { color: '#94a3b8', fontSize: 9, marginBottom: 2 } }, val % 1 === 0 ? val : '$' + val.toFixed(0)) : null,
          React.createElement('div', { style: { width: '100%', height: pct + '%', background: color, borderRadius: '3px 3px 0 0', minHeight: val > 0 ? 2 : 0, transition: 'height 0.3s' } }),
          (i % showEvery === 0) ? React.createElement('span', { style: { color: '#475569', fontSize: 8, marginTop: 3, whiteSpace: 'nowrap' } }, label.slice(5)) : null
        );
      })
    );
  };

  // Donut chart helper (CSS)
  var DonutChart = function(props) {
    var items = props.items || [];
    var total = items.reduce(function(s, i) { return s + i.value; }, 0) || 1;
    var cumPct = 0;
    var gradParts = [];
    items.forEach(function(item) {
      var pct = (item.value / total) * 100;
      gradParts.push(item.color + ' ' + cumPct + '% ' + (cumPct + pct) + '%');
      cumPct += pct;
    });

    return React.createElement('div', { style: { display: 'flex', alignItems: 'center', gap: 16 } },
      React.createElement('div', { style: { width: 100, height: 100, borderRadius: '50%', background: 'conic-gradient(' + gradParts.join(', ') + ')', position: 'relative', flexShrink: 0 } },
        React.createElement('div', { style: { position: 'absolute', inset: 25, borderRadius: '50%', background: '#1e293b', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#f1f5f9', fontWeight: 700, fontSize: 16 } }, total)
      ),
      React.createElement('div', { style: { display: 'flex', flexDirection: 'column', gap: 4 } },
        items.map(function(item, i) {
          return React.createElement('div', { key: i, style: { display: 'flex', alignItems: 'center', gap: 6, fontSize: 12 } },
            React.createElement('div', { style: { width: 8, height: 8, borderRadius: 2, background: item.color } }),
            React.createElement('span', { style: { color: '#94a3b8' } }, item.label + ': '),
            React.createElement('span', { style: { color: '#f1f5f9', fontWeight: 600 } }, item.value)
          );
        })
      )
    );
  };

  // Prepare chart data
  var dailyData = [];
  if (breakdown?.daily) {
    var keys = Object.keys(breakdown.daily.bookings || {}).sort();
    var sliceStart = period === '7d' ? Math.max(keys.length - 7, 0) : 0;
    keys.slice(sliceStart).forEach(function(k) {
      dailyData.push({
        label: k,
        bookings: breakdown.daily.bookings[k] || 0,
        revenue: breakdown.daily.revenue[k] || 0,
        clients: breakdown.daily.clients[k] || 0,
      });
    });
  }

  // MRR data
  var mrrData = mrr.map(function(h) { return { label: h.date, value: h.mrr, paid: h.paid, trial: h.trial }; });
  var slicedMrr = period === '7d' ? mrrData.slice(-7) : mrrData;

  var statusItems = breakdown?.statusCounts ? [
    { label: 'Confirmed', value: breakdown.statusCounts.confirmed || 0, color: '#22c55e' },
    { label: 'Pending', value: breakdown.statusCounts.pending || 0, color: '#f59e0b' },
    { label: 'Completed', value: breakdown.statusCounts.completed || 0, color: '#3b82f6' },
    { label: 'Cancelled', value: breakdown.statusCounts.cancelled || 0, color: '#ef4444' },
  ] : [];

  var typeItems = [];
  if (breakdown?.typeCounts) {
    var typeColors = { spa: '#ec4899', barbershop: '#f59e0b', auto_shop: '#3b82f6', smoke_shop: '#22c55e', salon: '#a78bfa', fitness: '#06b6d4', other: '#64748b' };
    Object.keys(breakdown.typeCounts).forEach(function(k) {
      typeItems.push({ label: k, value: breakdown.typeCounts[k], color: typeColors[k] || '#64748b' });
    });
  }

  var pipelineStages = pipeline ? [
    { label: 'Lead', count: (pipeline.lead || []).length, color: '#64748b' },
    { label: 'Demo', count: (pipeline.demo || []).length, color: '#3b82f6' },
    { label: 'Trial', count: (pipeline.trial || []).length, color: '#f59e0b' },
    { label: 'Paid', count: (pipeline.paid || []).length, color: '#22c55e' },
    { label: 'Churned', count: (pipeline.churned || []).length, color: '#ef4444' },
  ] : [];
  var maxPipeline = Math.max.apply(null, pipelineStages.map(function(s) { return s.count; }).concat([1]));

  return (
    React.createElement(PageShell, { title: 'Analytics', icon: PieChart, action:
      React.createElement('div', { style: { display: 'flex', gap: 6 } },
        ['7d', '30d'].map(function(p) {
          return React.createElement('button', {
            key: p,
            onClick: function() { setPeriod(p); },
            style: { padding: '5px 12px', borderRadius: 8, border: '1px solid ' + (period === p ? '#a78bfa' : '#334155'), background: period === p ? '#a78bfa22' : 'transparent', color: period === p ? '#a78bfa' : '#64748b', fontSize: 12, cursor: 'pointer', fontWeight: period === p ? 600 : 400 }
          }, p === '7d' ? '7 Days' : '30 Days');
        })
      )
    },
      // Top stats
      React.createElement('div', { style: { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(155px, 1fr))', gap: 12, marginBottom: 24 } },
        React.createElement(StatCard, { label: 'Total Bookings (30d)', value: loading ? '...' : (breakdown?.totals?.bookings || 0), icon: Calendar, color: '#3b82f6' }),
        React.createElement(StatCard, { label: 'Revenue (30d)', value: loading ? '...' : ('$' + (breakdown?.totals?.revenue || 0).toFixed(0)), icon: DollarSign, color: '#22c55e' }),
        React.createElement(StatCard, { label: 'New Clients (30d)', value: loading ? '...' : (breakdown?.totals?.clients || 0), icon: UserPlus, color: '#a78bfa' }),
        React.createElement(StatCard, { label: 'MRR', value: loading ? '...' : ('$' + (s.totalMRR || 0)), icon: TrendingUp, color: '#ec4899' }),
        React.createElement(StatCard, { label: 'Tenants', value: loading ? '...' : (s.totalTenants || 0), icon: Building2, color: '#f59e0b' }),
        React.createElement(StatCard, { label: 'Conversion', value: loading ? '...' : ((s.totalTenants > 0 ? Math.round(s.paidTenants / s.totalTenants * 100) : 0) + '%'), icon: ArrowUpRight, color: '#06b6d4' })
      ),

      // Charts row
      React.createElement('div', { style: { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginBottom: 20 } },
        // Revenue / Bookings chart
        React.createElement('div', { style: { background: '#1e293b', borderRadius: 12, padding: 20, border: '1px solid #334155' } },
          React.createElement('div', { style: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 } },
            React.createElement('h3', { style: { color: '#f1f5f9', fontSize: 14, fontWeight: 600, margin: 0 } }, chartView === 'revenue' ? 'Daily Revenue' : chartView === 'bookings' ? 'Daily Bookings' : 'New Clients'),
            React.createElement('div', { style: { display: 'flex', gap: 4 } },
              ['revenue', 'bookings', 'clients'].map(function(v) {
                return React.createElement('button', {
                  key: v, onClick: function() { setChartView(v); },
                  style: { padding: '3px 8px', borderRadius: 6, border: '1px solid ' + (chartView === v ? '#a78bfa' : '#334155'), background: chartView === v ? '#a78bfa22' : 'transparent', color: chartView === v ? '#a78bfa' : '#475569', fontSize: 11, cursor: 'pointer' }
                }, v.charAt(0).toUpperCase() + v.slice(1));
              })
            )
          ),
          loading ? React.createElement('div', { style: { height: 180, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#64748b' } }, 'Loading...') :
          React.createElement(BarChart, {
            data: dailyData,
            valueKey: chartView,
            color: chartView === 'revenue' ? '#22c55e' : chartView === 'bookings' ? '#3b82f6' : '#a78bfa',
            height: 180,
          })
        ),

        // MRR Trend
        React.createElement('div', { style: { background: '#1e293b', borderRadius: 12, padding: 20, border: '1px solid #334155' } },
          React.createElement('h3', { style: { color: '#f1f5f9', fontSize: 14, fontWeight: 600, marginBottom: 16 } }, 'MRR Trend'),
          loading ? React.createElement('div', { style: { height: 180, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#64748b' } }, 'Loading...') :
          React.createElement(BarChart, { data: slicedMrr, valueKey: 'value', color: '#ec4899', height: 180 })
        )
      ),

      // Middle row: Pipeline + Status + Type
      React.createElement('div', { style: { display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 16, marginBottom: 20 } },
        // Pipeline Funnel
        React.createElement('div', { style: { background: '#1e293b', borderRadius: 12, padding: 20, border: '1px solid #334155' } },
          React.createElement('h3', { style: { color: '#f1f5f9', fontSize: 14, fontWeight: 600, marginBottom: 16 } }, 'Pipeline'),
          pipelineStages.map(function(stage) {
            var w = Math.max((stage.count / maxPipeline) * 100, stage.count > 0 ? 8 : 0);
            return React.createElement('div', { key: stage.label, style: { marginBottom: 8 } },
              React.createElement('div', { style: { display: 'flex', justifyContent: 'space-between', marginBottom: 3 } },
                React.createElement('span', { style: { color: '#94a3b8', fontSize: 12 } }, stage.label),
                React.createElement('span', { style: { color: '#f1f5f9', fontSize: 12, fontWeight: 600 } }, stage.count)
              ),
              React.createElement('div', { style: { height: 8, borderRadius: 4, background: '#0f172a' } },
                React.createElement('div', { style: { height: '100%', width: w + '%', background: stage.color, borderRadius: 4, transition: 'width 0.4s' } })
              )
            );
          })
        ),

        // Booking Status
        React.createElement('div', { style: { background: '#1e293b', borderRadius: 12, padding: 20, border: '1px solid #334155' } },
          React.createElement('h3', { style: { color: '#f1f5f9', fontSize: 14, fontWeight: 600, marginBottom: 16 } }, 'Booking Status (30d)'),
          loading ? React.createElement('div', { style: { color: '#64748b', textAlign: 'center', padding: 20 } }, '...') :
          React.createElement(DonutChart, { items: statusItems })
        ),

        // Business Types
        React.createElement('div', { style: { background: '#1e293b', borderRadius: 12, padding: 20, border: '1px solid #334155' } },
          React.createElement('h3', { style: { color: '#f1f5f9', fontSize: 14, fontWeight: 600, marginBottom: 16 } }, 'Business Types'),
          loading ? React.createElement('div', { style: { color: '#64748b', textAlign: 'center', padding: 20 } }, '...') :
          React.createElement(DonutChart, { items: typeItems })
        )
      ),

      // Tenant Breakdown Table
      React.createElement('div', { style: { background: '#1e293b', borderRadius: 12, border: '1px solid #334155', overflow: 'hidden' } },
        React.createElement('div', { style: { padding: '16px 20px', borderBottom: '1px solid #334155' } },
          React.createElement('h3', { style: { color: '#f1f5f9', fontSize: 14, fontWeight: 600, margin: 0 } }, 'Tenant Breakdown (30 Days)')
        ),
        React.createElement('div', { style: { overflowX: 'auto' } },
          React.createElement('table', { style: { width: '100%', borderCollapse: 'collapse' } },
            React.createElement('thead', null,
              React.createElement('tr', { style: { borderBottom: '1px solid #334155' } },
                ['Business', 'Type', 'Plan', 'Bookings', 'Revenue', 'Clients', 'SMS Out'].map(function(h) {
                  return React.createElement('th', { key: h, style: { padding: '10px 16px', color: '#64748b', fontSize: 11, fontWeight: 600, textTransform: 'uppercase', textAlign: h === 'Business' ? 'left' : 'right' } }, h);
                })
              )
            ),
            React.createElement('tbody', null,
              (breakdown?.breakdown || []).map(function(t, idx) {
                var planColor = { trial: '#f59e0b', starter: '#3b82f6', standard: '#22c55e', professional: '#a78bfa' }[t.plan] || '#64748b';
                return React.createElement('tr', { key: t.id, style: { borderBottom: '1px solid #1e293b', background: idx % 2 === 0 ? 'transparent' : '#0f172a33' } },
                  React.createElement('td', { style: { padding: '10px 16px', color: '#f1f5f9', fontSize: 13, fontWeight: 500 } }, t.name),
                  React.createElement('td', { style: { padding: '10px 16px', color: '#94a3b8', fontSize: 12, textAlign: 'right' } }, t.type),
                  React.createElement('td', { style: { padding: '10px 16px', textAlign: 'right' } }, React.createElement('span', { style: { color: planColor, background: planColor + '22', padding: '2px 8px', borderRadius: 6, fontSize: 11, fontWeight: 600 } }, t.plan)),
                  React.createElement('td', { style: { padding: '10px 16px', color: '#f1f5f9', fontSize: 13, fontWeight: 600, textAlign: 'right' } }, t.bookings),
                  React.createElement('td', { style: { padding: '10px 16px', color: '#22c55e', fontSize: 13, fontWeight: 600, textAlign: 'right' } }, '$' + (t.revenue || 0).toFixed(0)),
                  React.createElement('td', { style: { padding: '10px 16px', color: '#f1f5f9', fontSize: 13, textAlign: 'right' } }, t.clients),
                  React.createElement('td', { style: { padding: '10px 16px', color: '#f1f5f9', fontSize: 13, textAlign: 'right' } }, t.smsOut)
                );
              })
            )
          )
        ),
        (!loading && (!breakdown?.breakdown || breakdown.breakdown.length === 0)) ?
          React.createElement('div', { style: { padding: 30, textAlign: 'center', color: '#64748b' } }, 'No tenant data yet') : null
      )
    )
  );
}

// ════════════════════════════════════════════════════════════════
// PLATFORM SETTINGS V3 — DB-backed config + health + links
// ════════════════════════════════════════════════════════════════
export function AdminSettings() {
  var [config, setConfig] = useState({
    founders_price: '99', standard_price: '199', multi_loc_founding_2_4: '84', multi_loc_founding_5: '74', multi_loc_standard_2_4: '114', multi_loc_standard_5: '94', sms_1k: '29', sms_3k: '49', sms_5k: '79', sms_included: '200', voip_price: '29',
    trial_days: '14', beta_slots: '10', beta_months_free: '3',
    super_admin_email: 'ivanjackm@protonmail.com', support_email: 'support@ivisionmarketing.com',
    company_name: 'IVision Marketing LLC', company_state: 'Nevada',
    maintenance_mode: false, registration_open: true, max_tenants: '100',
    default_sms_from: '', stripe_mode: 'test', welcome_sms: 'Welcome to {business}! Book online anytime.',
  });
  var [saved, setSaved] = useState(false);
  var [saving, setSaving] = useState(false);
  var [health, setHealth] = useState(null);
  var [loadingHealth, setLoadingHealth] = useState(true);
  var [activeSection, setActiveSection] = useState('pricing');

  useEffect(function() {
    // Load config from server
    axios.get('/api/admin/platform-config').then(function(r) {
      if (r.data.config) setConfig(function(prev) { return Object.assign({}, prev, r.data.config); });
    }).catch(function() {});
    // Load health
    axios.get('/api/admin/health').then(function(r) {
      setHealth(r.data);
      setLoadingHealth(false);
    }).catch(function() { setLoadingHealth(false); });
  }, []);

  var save = function() {
    setSaving(true);
    axios.post('/api/admin/platform-config', { config: config }).then(function() {
      setSaved(true); setSaving(false);
      setTimeout(function() { setSaved(false); }, 2500);
    }).catch(function() { setSaving(false); alert('Save failed'); });
  };

  var set = function(k) { return function(v) { setConfig(function(prev) { return Object.assign({}, prev, { [k]: typeof v === 'object' && v.target ? v.target.value : v }); }); }; };
  var toggle = function(k) { return function() { setConfig(function(prev) { return Object.assign({}, prev, { [k]: !prev[k] }); }); }; };

  var sections = [
    { key: 'pricing', label: 'Pricing Plans', icon: '💰' },
    { key: 'trial', label: 'Trial & Beta', icon: '🧪' },
    { key: 'company', label: 'Company', icon: '🏢' },
    { key: 'messaging', label: 'Messaging', icon: '📱' },
    { key: 'features', label: 'Feature Flags', icon: '🚀' },
    { key: 'health', label: 'Service Health', icon: '💚' },
    { key: 'links', label: 'Quick Links', icon: '🔗' },
  ];

  var healthStatus = function(name, ok) {
    return React.createElement('div', {
      style: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '12px 16px', borderBottom: '1px solid #334155' }
    },
      React.createElement('div', { style: { display: 'flex', alignItems: 'center', gap: 10 } },
        React.createElement('div', { style: { width: 10, height: 10, borderRadius: '50%', background: ok ? '#22c55e' : '#ef4444' } }),
        React.createElement('span', { style: { color: '#f1f5f9', fontSize: 14 } }, name)
      ),
      React.createElement('span', { style: { color: ok ? '#22c55e' : '#ef4444', fontSize: 12, fontWeight: 600 } }, ok ? 'Operational' : 'Down')
    );
  };

  var linkBtn = function(label, url, icon) {
    return React.createElement('a', {
      href: url, target: '_blank', rel: 'noopener noreferrer',
      style: { display: 'flex', alignItems: 'center', gap: 8, padding: '12px 16px', background: '#0f172a', borderRadius: 8, border: '1px solid #334155', color: '#94a3b8', fontSize: 13, textDecoration: 'none', transition: 'border-color 0.2s' },
      onMouseEnter: function(e) { e.currentTarget.style.borderColor = '#a78bfa'; e.currentTarget.style.color = '#f1f5f9'; },
      onMouseLeave: function(e) { e.currentTarget.style.borderColor = '#334155'; e.currentTarget.style.color = '#94a3b8'; }
    },
      React.createElement('span', { style: { fontSize: 18 } }, icon),
      React.createElement('div', null,
        React.createElement('div', { style: { fontWeight: 500, color: 'inherit' } }, label),
        React.createElement('div', { style: { fontSize: 11, color: '#475569', marginTop: 2 } }, url.replace('https://', '').split('/')[0])
      ),
      React.createElement(ExternalLink, { size: 14, style: { marginLeft: 'auto' } })
    );
  };

  var ToggleSwitch = function(props) {
    return React.createElement('div', {
      onClick: props.onChange,
      style: { width: 44, height: 24, borderRadius: 12, background: props.checked ? '#22c55e' : '#334155', cursor: 'pointer', position: 'relative', transition: 'background 0.2s' }
    },
      React.createElement('div', { style: { width: 18, height: 18, borderRadius: '50%', background: '#fff', position: 'absolute', top: 3, left: props.checked ? 23 : 3, transition: 'left 0.2s' } })
    );
  };

  return (
    React.createElement(PageShell, { title: 'Platform Settings', icon: Shield, action:
      React.createElement(Btn, { onClick: save, color: saved ? '#22c55e' : '#a78bfa', disabled: saving },
        saved ? React.createElement(React.Fragment, null, React.createElement(CheckCircle, { size: 16 }), ' Saved!') :
        saving ? 'Saving...' :
        React.createElement(React.Fragment, null, React.createElement(Save, { size: 16 }), ' Save to Server')
      )
    },
      React.createElement('div', { style: { display: 'flex', gap: 20 } },
        // Left nav
        React.createElement('div', { style: { width: 200, flexShrink: 0 } },
          sections.map(function(sec) {
            var active = activeSection === sec.key;
            return React.createElement('button', {
              key: sec.key,
              onClick: function() { setActiveSection(sec.key); },
              style: { display: 'flex', alignItems: 'center', gap: 8, width: '100%', padding: '10px 14px', marginBottom: 4, borderRadius: 8, border: 'none', background: active ? '#a78bfa22' : 'transparent', color: active ? '#a78bfa' : '#94a3b8', fontSize: 13, cursor: 'pointer', textAlign: 'left', fontWeight: active ? 600 : 400, borderLeft: active ? '3px solid #a78bfa' : '3px solid transparent' }
            }, sec.icon + ' ' + sec.label);
          })
        ),

        // Right content
        React.createElement('div', { style: { flex: 1 } },

          // Pricing
          activeSection === 'pricing' ? React.createElement('div', { style: { background: '#1e293b', borderRadius: 12, padding: 24, border: '1px solid #334155' } },
            React.createElement('h3', { style: { color: '#f1f5f9', fontSize: 16, fontWeight: 600, marginBottom: 16, marginTop: 0 } }, '💰 Pricing Plans'),
            React.createElement('div', { style: { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 16 } },
              React.createElement(FormField, { label: 'Founders Rate ($/mo)', value: config.founders_price, onChange: set('founders_price') }),
              React.createElement(FormField, { label: 'Standard Rate ($/mo)', value: config.standard_price, onChange: set('standard_price') }),
              React.createElement(FormField, { label: 'Multi-Loc Founding 2-4 (+$/mo)', value: config.multi_loc_founding_2_4, onChange: set('multi_loc_founding_2_4') }),
              React.createElement(FormField, { label: 'Multi-Loc Founding 5+ (+$/mo)', value: config.multi_loc_founding_5, onChange: set('multi_loc_founding_5') })
            ),
            React.createElement('h4', { style: { color: '#94a3b8', fontSize: 13, fontWeight: 600, marginTop: 20, marginBottom: 12 } }, 'STANDARD MULTI-LOCATION'),
            React.createElement('div', { style: { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 16 } },
              React.createElement(FormField, { label: 'Multi-Loc Standard 2-4 (+$/mo)', value: config.multi_loc_standard_2_4, onChange: set('multi_loc_standard_2_4') }),
              React.createElement(FormField, { label: 'Multi-Loc Standard 5+ (+$/mo)', value: config.multi_loc_standard_5, onChange: set('multi_loc_standard_5') })
            ),
            React.createElement('h4', { style: { color: '#94a3b8', fontSize: 13, fontWeight: 600, marginTop: 20, marginBottom: 12 } }, 'SMS ADD-ONS'),
            React.createElement('div', { style: { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 16 } },
              React.createElement(FormField, { label: 'Free SMS Included', value: config.sms_included, onChange: set('sms_included') }),
              React.createElement(FormField, { label: 'SMS 1K ($/mo)', value: config.sms_1k, onChange: set('sms_1k') }),
              React.createElement(FormField, { label: 'SMS 3K ($/mo)', value: config.sms_3k, onChange: set('sms_3k') }),
              React.createElement(FormField, { label: 'SMS 5K ($/mo)', value: config.sms_5k, onChange: set('sms_5k') })
            ),
            React.createElement('h4', { style: { color: '#94a3b8', fontSize: 13, fontWeight: 600, marginTop: 20, marginBottom: 12 } }, 'COMING SOON'),
            React.createElement('div', { style: { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 16 } },
              React.createElement(FormField, { label: 'VoIP Phone ($/mo)', value: config.voip_price, onChange: set('voip_price') })
            ),
            React.createElement('div', { style: { marginTop: 16, padding: 12, background: '#0f172a', borderRadius: 8, color: '#64748b', fontSize: 12 } },
              'Founders $99/mo (locked forever) · Standard $199/mo · All features included on every plan · Update Stripe products separately for live billing.'
            )
          ) : null,

          // Trial & Beta
          activeSection === 'trial' ? React.createElement('div', { style: { background: '#1e293b', borderRadius: 12, padding: 24, border: '1px solid #334155' } },
            React.createElement('h3', { style: { color: '#f1f5f9', fontSize: 16, fontWeight: 600, marginBottom: 16, marginTop: 0 } }, '🧪 Trial & Beta'),
            React.createElement('div', { style: { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 16 } },
              React.createElement(FormField, { label: 'Trial Days', value: config.trial_days, onChange: set('trial_days') }),
              React.createElement(FormField, { label: 'Beta Slots (free)', value: config.beta_slots, onChange: set('beta_slots') }),
              React.createElement(FormField, { label: 'Beta Free Months', value: config.beta_months_free, onChange: set('beta_months_free') }),
              React.createElement(FormField, { label: 'Max Tenants', value: config.max_tenants, onChange: set('max_tenants') })
            )
          ) : null,

          // Company
          activeSection === 'company' ? React.createElement('div', { style: { background: '#1e293b', borderRadius: 12, padding: 24, border: '1px solid #334155' } },
            React.createElement('h3', { style: { color: '#f1f5f9', fontSize: 16, fontWeight: 600, marginBottom: 16, marginTop: 0 } }, '🏢 Company Info'),
            React.createElement('div', { style: { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 16 } },
              React.createElement(FormField, { label: 'Company Name', value: config.company_name, onChange: set('company_name') }),
              React.createElement(FormField, { label: 'State', value: config.company_state, onChange: set('company_state') }),
              React.createElement(FormField, { label: 'Admin Email', value: config.super_admin_email, onChange: set('super_admin_email') }),
              React.createElement(FormField, { label: 'Support Email', value: config.support_email, onChange: set('support_email') })
            )
          ) : null,

          // Messaging
          activeSection === 'messaging' ? React.createElement('div', { style: { background: '#1e293b', borderRadius: 12, padding: 24, border: '1px solid #334155' } },
            React.createElement('h3', { style: { color: '#f1f5f9', fontSize: 16, fontWeight: 600, marginBottom: 16, marginTop: 0 } }, '📱 Messaging'),
            React.createElement('div', { style: { display: 'grid', gridTemplateColumns: '1fr', gap: 16 } },
              React.createElement(FormField, { label: 'Default SMS From Number', value: config.default_sms_from, onChange: set('default_sms_from'), placeholder: '+1xxxxxxxxxx' }),
              React.createElement(FormField, { label: 'Welcome SMS Template', value: config.welcome_sms, onChange: set('welcome_sms'), placeholder: 'Welcome to {business}!' }),
              React.createElement(FormField, { label: 'Stripe Mode', value: config.stripe_mode, onChange: set('stripe_mode'), options: [{ value: 'test', label: 'Test (Sandbox)' }, { value: 'live', label: 'Live' }] })
            )
          ) : null,

          // Feature Flags
          activeSection === 'features' ? React.createElement('div', { style: { background: '#1e293b', borderRadius: 12, padding: 24, border: '1px solid #334155' } },
            React.createElement('h3', { style: { color: '#f1f5f9', fontSize: 16, fontWeight: 600, marginBottom: 16, marginTop: 0 } }, '🚀 Feature Flags'),
            [
              { key: 'maintenance_mode', label: 'Maintenance Mode', desc: 'Show maintenance page to all tenants' },
              { key: 'registration_open', label: 'Open Registration', desc: 'Allow new signups' },
            ].map(function(flag) {
              return React.createElement('div', {
                key: flag.key,
                style: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '14px 0', borderBottom: '1px solid #334155' }
              },
                React.createElement('div', null,
                  React.createElement('div', { style: { color: '#f1f5f9', fontSize: 14, fontWeight: 500 } }, flag.label),
                  React.createElement('div', { style: { color: '#64748b', fontSize: 12, marginTop: 2 } }, flag.desc)
                ),
                React.createElement(ToggleSwitch, { checked: !!config[flag.key], onChange: toggle(flag.key) })
              );
            })
          ) : null,

          // Health
          activeSection === 'health' ? React.createElement('div', { style: { background: '#1e293b', borderRadius: 12, border: '1px solid #334155', overflow: 'hidden' } },
            React.createElement('div', { style: { padding: '16px 20px', borderBottom: '1px solid #334155', display: 'flex', justifyContent: 'space-between', alignItems: 'center' } },
              React.createElement('h3', { style: { color: '#f1f5f9', fontSize: 16, fontWeight: 600, margin: 0 } }, '💚 Service Health'),
              React.createElement('span', { style: { color: '#64748b', fontSize: 11 } }, loadingHealth ? 'Checking...' : 'Last checked: just now')
            ),
            loadingHealth ? React.createElement('div', { style: { padding: 40, textAlign: 'center', color: '#64748b' } }, 'Checking services...') :
            React.createElement('div', null,
              healthStatus('API Backend', health?.status === 'healthy' || health?.services?.api),
              healthStatus('Database (Supabase)', health?.services?.database !== false),
              healthStatus('Twilio SMS', health?.services?.twilio !== false),
              healthStatus('Stripe Payments', health?.services?.stripe !== false),
              healthStatus('Vercel Frontend', true)
            )
          ) : null,

          // Quick Links
          activeSection === 'links' ? React.createElement('div', { style: { background: '#1e293b', borderRadius: 12, padding: 24, border: '1px solid #334155' } },
            React.createElement('h3', { style: { color: '#f1f5f9', fontSize: 16, fontWeight: 600, marginBottom: 16, marginTop: 0 } }, '🔗 Quick Links'),
            React.createElement('div', { style: { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 } },
              linkBtn('Stripe Dashboard', 'https://dashboard.stripe.com', '💳'),
              linkBtn('Supabase Console', 'https://supabase.com/dashboard', '🗄️'),
              linkBtn('Railway Backend', 'https://railway.app/dashboard', '🚂'),
              linkBtn('Vercel Frontend', 'https://vercel.com/dashboard', '▲'),
              linkBtn('Twilio Console', 'https://console.twilio.com', '📞'),
              linkBtn('Live Site', 'https://ivisionmarketing.com', '🌐')
            )
          ) : null
        )
      )
    )
  );
}

